import React, { useCallback, useEffect, useRef, useState } from "react";

import {
  Check,
  ChevronDown,
  ChevronRight,
  Eye,
  Globe2,
  MoreVertical,
  UserRound,
} from "lucide-react";
import { createPortal } from "react-dom";
import { useNavigate, useParams } from "react-router-dom";
import { useLocation } from "react-router-dom";

import { resetAlwaysOnRowReveal } from "./alwaysOnRowReveal";
import AccountBriefingLegend from "./components/AccountBriefingLegend";
import AnyTab from "./components/AnyTab";
import type {
  BirdsEyeClusterId,
  BirdsEyeClusterSummary,
} from "./components/BirdsEyeOverview";
import type {
  BriefingParentTab,
  BriefingWorkspaceTab,
} from "./components/BriefingWorkspace";
import CreateWatchlistModal, {
  type CreateWatchlistModalSuccess,
  type EditWatchListTarget,
} from "./components/CreateWatchlistModal";
import KpiDataUnavailableBanner from "./components/KpiDataUnavailableBanner";
import MonitoredTab from "./components/MonitoredTab";
import MonitoredWatchlistSidebar, {
  type MonitoredSidebarTab,
} from "./components/MonitoredWatchlistSidebar";
import PortfolioSponsorOnlyState from "./components/PortfolioSponsorOnlyState";
import PortfolioTab from "./components/PortfolioTab";
import SaveAccountsSuccessModal, {
  type SaveAccountsSuccessType,
} from "./components/SaveAccountsSuccessModal";
import {
  accountsForMonitoredBriefing,
  canAccessExecutiveMonitoredTab,
  getExecutiveAccountIds,
  getMonitoredOthersAccountIds,
  getWatchListCustomerIds,
  mapExecutiveSidebarAccounts,
  mapMonitoredOthersSidebarAccounts,
  resolveAccountRecordId,
} from "./utils/watchListAccounts";
import SearchInput from "../../../Components/Search/SearchInput";
import TabsComponent from "../../../Components/Tab";
import { scrollChainRef } from "../../../Hooks/useScrollChain";
import {
  useHawkeyeMetrics,
  type HawkeyeMetricsAccountsInput,
} from "../../../intelligence-briefing-library/hooks/useHawkeyeMetrics";
import { useAppDispatch, useAppSelector } from "../../../Store/Hooks";
import {
  fetchMonitoredAccounts,
  fetchExecutiveAccounts,
} from "../../../Store/Reducers/AlwaysOnChannelSlice";
import {
  fetchAssignedAccounts,
  type AssignedAccount,
} from "../../../Store/Reducers/CustomerWorkspaceSlice";
import {
  fetchAccountsWithSponsors,
  fetchWatchLists,
} from "../../../Store/Reducers/OpsSlice";
import { fetchHealthScoreThresholds } from "../../../Store/Reducers/VendorAdminSlice";
import {
  isAccountTeamRole,
  isRole,
  ROLES,
} from "../../../Utils/Constants/RoleConstants";
import { formatCompactCurrency } from "../../../Utils/Helper/formatCompactCurrency";
import { getInitials } from "../../../Utils/Helper/Helper";
import {
  STORAGE_KEYS,
  getFromLocalStorage,
} from "../../../Utils/Helper/LocalStorage";

import "./style.scss";

/**
 * Overall page scroll — sticky on tab:
 * This Attention (active tab) row is one viewport tall and position:sticky
 * at top:0 inside the page scroller. Page scroll moves the legend/header
 * away; once this row reaches the top of the scrollport it pins and stays
 * filling the viewport ("sticky on tab"). Inner Accounts / Insights panels
 * then own further wheel scroll. Sticky only holds while the scroll parent
 * still has height below this node — if this is the last child, add a
 * trailing spacer (or equivalent) so sticky can engage instead of unsticking
 * immediately.
 */
const ATTENTION_COLUMNS_H = "h-screen";
const BIRDS_EYE_CLUSTER_ORDER: BirdsEyeClusterId[] = [
  "renewal-risk",
  "high-adoption-mixed",
  "strong-performers",
  "kpi-data-not-available",
];

const BIRDS_EYE_CLUSTER_FALLBACK_META: Record<
  BirdsEyeClusterId,
  { label: string; accentColor: string; textColor: string }
> = {
  "renewal-risk": {
    label: "Renewal Risk",
    accentColor: "#ef4444",
    textColor: "#dc2626",
  },
  "high-adoption-mixed": {
    label: "High Adoption / Mixed",
    accentColor: "#eab308",
    textColor: "#eab308",
  },
  "strong-performers": {
    label: "Strong Performers",
    accentColor: "#22c55e",
    textColor: "#16a34a",
  },
  "kpi-data-not-available": {
    label: "KPIs Data not available",
    accentColor: "#94a3b8",
    textColor: "#64748b",
  },
};
const normalizeStringArray = (values: string[]) =>
  Array.from(new Set(values)).sort((left, right) => left.localeCompare(right));
const areStringArraysEqual = (left: string[], right: string[]) =>
  left.length === right.length &&
  left.every((value, index) => value === right[index]);

const getAccountNameForSort = (account: any) =>
  String(account?.name || account?.account_name || "").trim();

const getSponsorNameForSort = (account: any) =>
  String(account?.primary_sponsors || "").trim() || "Unassigned";

const mapAssignedAccountToDashboardShape = (
  account: AssignedAccount,
  sourceAccount?: any
) => ({
  customer_id: account.customer_id,
  name: account.customer_name,
  account_name: account.customer_name,
  industry: account.industry,
  tier: account.tier,
  tiers: account.tier,
  role_type: account.role_type,
  region: account.region || sourceAccount?.region,
  location: account.location || sourceAccount?.location,
  city: sourceAccount?.city,
  country: sourceAccount?.country,
  latitude: sourceAccount?.latitude,
  longitude: sourceAccount?.longitude,
  primary_sponsors: account.primary_sponsors,
  primary_sponsors_id: account.primary_sponsors_id,
  secondary_sponsors: account.secondary_sponsors,
  secondary_sponsors_id: account.secondary_sponsors_id,
  revenue: account.revenue,
  renewal_date: account.renewal_date,
  customer_level: account.customer_level,
  alignment_level: account.alignment_level,
  is_ibbriefing_generated: sourceAccount?.is_ibbriefing_generated,
  assigned_at: sourceAccount?.assigned_at,
  assigned_on: sourceAccount?.assigned_on,
});

const canAccessMyPortfolio = (
  role: string | { name?: string } | null | undefined
): boolean => isRole(role, ROLES.EXECUTIVE_SPONSOR) || isAccountTeamRole(role);

type AccountFilterTabId = "any" | "portfolio" | "monitored";

type AnyTabSelection = {
  highlightedIds: string[];
  sponsorFilter: {
    type: "all" | "unassigned" | "sponsor";
    sponsorId?: string;
    sponsorName?: string;
  };
};

type PortfolioTabSelection = {
  highlightedIds: string[];
  customerIds: string[];
};

type MonitoredChildSelection = {
  watchlistId: string | null;
  monitoredAccountIds: string[];
  executiveAccountIds: string[];
  isAccountSelectionActive: boolean;
};

type MonitoredTabSelection = {
  activeSidebarTab: MonitoredSidebarTab;
  executive: MonitoredChildSelection;
  watchlist: MonitoredChildSelection;
  others: MonitoredChildSelection;
};

/** Per left-tab selection boxes — switching tabs restores that tab's box. */
type TabSelectionBoxes = {
  any: AnyTabSelection;
  portfolio: PortfolioTabSelection;
  monitored: MonitoredTabSelection;
};

const createEmptyMonitoredChildSelection = (): MonitoredChildSelection => ({
  watchlistId: null,
  monitoredAccountIds: [],
  executiveAccountIds: [],
  isAccountSelectionActive: false,
});

const createEmptyMonitoredTabSelection = (): MonitoredTabSelection => ({
  activeSidebarTab: "others",
  executive: createEmptyMonitoredChildSelection(),
  watchlist: createEmptyMonitoredChildSelection(),
  others: createEmptyMonitoredChildSelection(),
});

const createEmptyTabSelectionBoxes = (): TabSelectionBoxes => ({
  any: {
    highlightedIds: [],
    sponsorFilter: { type: "all" },
  },
  portfolio: {
    highlightedIds: [],
    customerIds: [],
  },
  monitored: createEmptyMonitoredTabSelection(),
});

/** Snapshot live monitored selection into the active child box shape. */
const captureMonitoredChildSelection = (
  sidebarTab: MonitoredSidebarTab,
  state: {
    watchlistId: string | null;
    monitoredAccountIds: string[];
    executiveAccountIds: string[];
    isAccountSelectionActive: boolean;
  }
): MonitoredChildSelection => {
  if (sidebarTab === "watchlist") {
    return {
      watchlistId: state.watchlistId,
      // Persist Temp Public Data / KPI row highlights for this watchlist child.
      monitoredAccountIds: [...state.monitoredAccountIds],
      executiveAccountIds: [],
      isAccountSelectionActive: state.isAccountSelectionActive,
    };
  }
  if (sidebarTab === "others") {
    return {
      watchlistId: null,
      monitoredAccountIds: [...state.monitoredAccountIds],
      executiveAccountIds: [],
      isAccountSelectionActive: state.isAccountSelectionActive,
    };
  }
  return {
    watchlistId: null,
    monitoredAccountIds: [],
    executiveAccountIds: [...state.executiveAccountIds],
    isAccountSelectionActive: state.isAccountSelectionActive,
  };
};

const Dashboard: React.FC = () => {
  const location = useLocation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { orgSlug } = useParams<{ orgSlug?: string }>();
  const appliedDeepLinkAccountRef = useRef<string | null>(null);
  const {
    accounts: { data: accountsData, loading: accountsLoading },
    watchLists: { data: watchListsData, loading: watchListsLoading },
  } = useAppSelector(state => state.ops);
  const {
    monitorAccounts: {
      items: monitoredAccountsData,
      loading: monitoredAccountsLoading,
      error: monitorAccountsError,
    },
    executiveAccounts: {
      items: executiveAccountsData,
      loading: executiveAccountsLoading,
      error: executiveAccountsError,
    },
  } = useAppSelector(state => state.alwaysOnChannel);
  const {
    assignedAccounts: {
      items: assignedAccountsItems,
      loading: assignedAccountsLoading,
    },
  } = useAppSelector(state => state.customerWorkspace);

  const [activeTab] = useState("attention");
  const [searchQuery, setSearchQuery] = useState("");
  const [accountFilterTab, setAccountFilterTab] = useState(() => {
    const user = getFromLocalStorage(STORAGE_KEYS.LOGGED_IN_USER);
    return canAccessMyPortfolio(user?.role) ? "portfolio" : "any";
  });
  const [selectedWatchlistCustomerIds, setSelectedWatchlistCustomerIds] =
    useState<string[]>([]);
  const [selectedMonitoredWatchlistId, setSelectedMonitoredWatchlistId] =
    useState<string | null>(null);
  const [selectedMonitoredAccountIds, setSelectedMonitoredAccountIds] =
    useState<string[]>([]);
  const [selectedExecutiveAccountIds, setSelectedExecutiveAccountIds] =
    useState<string[]>([]);
  const [monitoredSidebarTab, setMonitoredSidebarTab] =
    useState<MonitoredSidebarTab>("others");
  const [highlightedAttentionAccountIds, setHighlightedAttentionAccountIds] =
    useState<string[]>([]);
  /** On by default: KPI data first (no-data at bottom). Off: all accounts A–Z. */
  const [showUnavailableKpiData, setShowUnavailableKpiData] = useState(true);
  const [kpiDataCounts, setKpiDataCounts] = useState({
    available: 0,
    unavailable: 0,
    loading: true,
  });
  /** Account IDs lacking Enterprise KPI data — used to sort left Accounts list. */
  const [accountsWithoutKpiData, setAccountsWithoutKpiData] = useState<
    string[]
  >([]);
  /** On by default: briefing data first (generating at bottom). Off: all A–Z. */
  const [showUnavailableBriefingData, setShowUnavailableBriefingData] =
    useState(true);
  const [briefingDataCounts, setBriefingDataCounts] = useState({
    available: 0,
    unavailable: 0,
    loading: true,
  });
  /** Account IDs with no Business Health briefing — grouped below when toggle is on. */
  const [accountsWithoutBriefingData, setAccountsWithoutBriefingData] =
    useState<string[]>([]);
  const [openAccountMenuKey, setOpenAccountMenuKey] = useState<string | null>(
    null
  );
  const [openAccountMenuRect, setOpenAccountMenuRect] =
    useState<DOMRect | null>(null);
  const accountMenuContainerRef = useRef<HTMLDivElement | null>(null);
  const [
    isMonitoredAccountSelectionActive,
    setIsMonitoredAccountSelectionActive,
  ] = useState(false);
  const [isCreateWatchlistModalOpen, setIsCreateWatchlistModalOpen] =
    useState(false);
  /** Pre-selected account IDs for CreateWatchlistModal only — must not change portfolio/KPI scope. */
  const [createWatchlistInitialIds, setCreateWatchlistInitialIds] = useState<
    string[]
  >([]);
  const [editWatchListTarget, setEditWatchListTarget] =
    useState<EditWatchListTarget | null>(null);
  const [pendingCreatedWatchlistName, setPendingCreatedWatchlistName] =
    useState<string | null>(null);
  const [saveAccountsSuccess, setSaveAccountsSuccess] = useState<{
    type: SaveAccountsSuccessType;
    watchlistName?: string;
  } | null>(null);
  const [selectedSponsorFilter, setSelectedSponsorFilter] = useState<{
    type: "all" | "unassigned" | "sponsor";
    sponsorId?: string;
    sponsorName?: string;
  }>({ type: "all" });
  const [selectedSponsorRoleTab, setSelectedSponsorRoleTab] = useState<
    "primary" | "secondary"
  >("primary");
  /** Keeps Bird's Eye vs Attention when switching Any Portfolio ↔ My Portfolio. */
  const [briefingParentTab, setBriefingParentTab] =
    useState<BriefingParentTab>("attention");
  /** Keeps Temp Public Data vs Enterprise KPIs across portfolio tab switches. */
  const [briefingSubTab, setBriefingSubTab] =
    useState<BriefingWorkspaceTab>("temp-public");

  // Next entry to Always On can run the one-time matrix reveal again.
  useEffect(() => {
    return () => {
      resetAlwaysOnRowReveal();
    };
  }, []);

  const [birdsEyeClusterGroups, setBirdsEyeClusterGroups] = useState<
    BirdsEyeClusterSummary[]
  >([]);
  const [selectedBirdsEyeAccountIds, setSelectedBirdsEyeAccountIds] = useState<
    string[]
  >([]);
  const [expandedBirdsEyeClusterIds, setExpandedBirdsEyeClusterIds] = useState<
    BirdsEyeClusterId[]
  >([BIRDS_EYE_CLUSTER_ORDER[0]]);

  /** Saved selections per Accounts tab (Any / My Portfolio / Monitored). */
  const tabSelectionBoxesRef = useRef<TabSelectionBoxes>(
    createEmptyTabSelectionBoxes()
  );
  /** Bird's Eye picks shared across Any ↔ My Portfolio; cleared on Monitored. */
  const anyPortfolioBirdsEyeRef = useRef<string[]>([]);
  const visitedAccountFilterTabsRef = useRef<Set<AccountFilterTabId>>(
    new Set()
  );
  /** First Monitored visit: wait for executive accounts before picking Executive vs Others. */
  const needsMonitoredDefaultTabResolveRef = useRef(false);
  /** True once executive fetch has been in-flight while on Monitored (avoids resolving too early). */
  const seenExecutiveLoadingOnMonitoredRef = useRef(false);
  /** Deep-link from Account Synopsis: highlight/select this account (keep full portfolio scope). */
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const deepLinkAccountId = params.get("accountId")?.trim() || "";
    if (!deepLinkAccountId) {
      appliedDeepLinkAccountRef.current = null;
      return;
    }
    if (appliedDeepLinkAccountRef.current === deepLinkAccountId) return;
    appliedDeepLinkAccountRef.current = deepLinkAccountId;

    setBriefingParentTab("attention");
    setHighlightedAttentionAccountIds([deepLinkAccountId]);
  }, [location.search]);

  const syncSelectedBirdsEyeAccountIds = React.useCallback(
    (accountIds: string[]) => {
      const normalizedAccountIds = normalizeStringArray(accountIds);
      setSelectedBirdsEyeAccountIds(prev =>
        areStringArraysEqual(prev, normalizedAccountIds)
          ? prev
          : normalizedAccountIds
      );
    },
    []
  );

  /**
   * Any / My Portfolio: keep Attention sidebar highlights and Bird's Eye
   * selection in sync so switching Attention ↔ Bird's Eye preserves picks.
   */
  const syncAnyPortfolioAccountSelection = React.useCallback(
    (accountIds: string[]) => {
      const normalizedAccountIds = normalizeStringArray(accountIds);
      setHighlightedAttentionAccountIds(prev =>
        areStringArraysEqual(prev, normalizedAccountIds)
          ? prev
          : normalizedAccountIds
      );
      setSelectedBirdsEyeAccountIds(prev =>
        areStringArraysEqual(prev, normalizedAccountIds)
          ? prev
          : normalizedAccountIds
      );
      anyPortfolioBirdsEyeRef.current = normalizedAccountIds;
    },
    []
  );

  /** Current Monitored Attention highlight IDs for the active child tab. */
  const getMonitoredHighlightIds = React.useCallback(() => {
    if (monitoredSidebarTab === "executive") {
      return selectedExecutiveAccountIds;
    }
    if (
      monitoredSidebarTab === "others" ||
      monitoredSidebarTab === "watchlist"
    ) {
      return selectedMonitoredAccountIds;
    }
    return [];
  }, [
    monitoredSidebarTab,
    selectedExecutiveAccountIds,
    selectedMonitoredAccountIds,
  ]);

  /**
   * Monitored: keep Attention / KPI highlights and Bird's Eye selection in sync
   * for the active child tab (Executive / Watchlist / Others).
   */
  const syncMonitoredAccountSelection = React.useCallback(
    (accountIds: string[]) => {
      const normalizedAccountIds = normalizeStringArray(accountIds);

      if (
        monitoredSidebarTab === "others" ||
        monitoredSidebarTab === "watchlist"
      ) {
        if (monitoredSidebarTab === "others") {
          setSelectedMonitoredWatchlistId(null);
        }
        setSelectedExecutiveAccountIds([]);
        setIsMonitoredAccountSelectionActive(true);
        setSelectedMonitoredAccountIds(prev =>
          areStringArraysEqual(prev, normalizedAccountIds)
            ? prev
            : normalizedAccountIds
        );
      } else if (monitoredSidebarTab === "executive") {
        setSelectedMonitoredWatchlistId(null);
        setSelectedMonitoredAccountIds([]);
        setIsMonitoredAccountSelectionActive(true);
        setSelectedExecutiveAccountIds(prev =>
          areStringArraysEqual(prev, normalizedAccountIds)
            ? prev
            : normalizedAccountIds
        );
      }

      setSelectedBirdsEyeAccountIds(prev =>
        areStringArraysEqual(prev, normalizedAccountIds)
          ? prev
          : normalizedAccountIds
      );
    },
    [monitoredSidebarTab]
  );

  const handleBriefingParentTabChange = React.useCallback(
    (tab: BriefingParentTab) => {
      if (tab === "birds-eye") {
        if (accountFilterTab === "monitored") {
          const monitoredHighlights = getMonitoredHighlightIds();
          if (monitoredHighlights.length > 0) {
            syncSelectedBirdsEyeAccountIds(monitoredHighlights);
          }
        } else if (highlightedAttentionAccountIds.length > 0) {
          // Carry Attention / sidebar selection into Bird's Eye.
          syncSelectedBirdsEyeAccountIds(highlightedAttentionAccountIds);
          anyPortfolioBirdsEyeRef.current = [...highlightedAttentionAccountIds];
        }
      }

      if (tab === "attention" || tab === "signals") {
        if (
          accountFilterTab === "monitored" &&
          selectedBirdsEyeAccountIds.length > 0
        ) {
          // Carry Bird's Eye selection back into Monitored Attention highlights.
          syncMonitoredAccountSelection(selectedBirdsEyeAccountIds);
        } else if (
          accountFilterTab !== "monitored" &&
          selectedBirdsEyeAccountIds.length > 0
        ) {
          // Carry Bird's Eye selection back into Attention highlights.
          setHighlightedAttentionAccountIds(prev =>
            areStringArraysEqual(prev, selectedBirdsEyeAccountIds)
              ? prev
              : [...selectedBirdsEyeAccountIds]
          );
        }
      }

      setBriefingParentTab(tab);
    },
    [
      accountFilterTab,
      getMonitoredHighlightIds,
      highlightedAttentionAccountIds,
      selectedBirdsEyeAccountIds,
      syncMonitoredAccountSelection,
      syncSelectedBirdsEyeAccountIds,
    ]
  );

  const handleBriefingSubTabChange = React.useCallback(
    (tab: BriefingWorkspaceTab) => {
      setBriefingSubTab(tab);

      const params = new URLSearchParams(location.search);
      if (tab === "kpi") {
        params.set("briefingTab", "kpi");
      } else if (tab === "temp-public") {
        params.set("briefingTab", "temp-public");
      } else {
        params.delete("briefingTab");
      }
      const nextSearch = params.toString();
      navigate(
        {
          pathname: location.pathname,
          search: nextSearch ? `?${nextSearch}` : "",
        },
        { replace: true }
      );
    },
    [location.pathname, location.search, navigate]
  );

  /** KPI availability grouping applies only on Attention → Account Health. */
  const isEnterpriseKpiAttention =
    briefingParentTab === "attention" && briefingSubTab === "kpi";
  const sortAccountsByKpiAvailability =
    isEnterpriseKpiAttention && showUnavailableKpiData;
  const isBusinessHealthAttention =
    briefingParentTab === "attention" && briefingSubTab === "temp-public";
  const groupAccountsByBriefingAvailability =
    isBusinessHealthAttention && showUnavailableBriefingData;

  const toggleBirdsEyeGroupSelection = React.useCallback(
    (accountIds: string[]) => {
      if (accountIds.length === 0) return;

      const computeNext = (prev: string[]) => {
        const allSelected = accountIds.every(accountId =>
          prev.includes(accountId)
        );
        return allSelected
          ? prev.filter(accountId => !accountIds.includes(accountId))
          : normalizeStringArray([...prev, ...accountIds]);
      };

      if (accountFilterTab === "monitored") {
        syncMonitoredAccountSelection(computeNext(selectedBirdsEyeAccountIds));
        return;
      }

      setSelectedBirdsEyeAccountIds(computeNext);
    },
    [
      accountFilterTab,
      selectedBirdsEyeAccountIds,
      syncMonitoredAccountSelection,
    ]
  );

  const clearBirdsEyeGroupSelection = React.useCallback(
    (accountIds: string[]) => {
      if (accountIds.length === 0) return;

      if (accountFilterTab === "monitored") {
        syncMonitoredAccountSelection(
          selectedBirdsEyeAccountIds.filter(
            accountId => !accountIds.includes(accountId)
          )
        );
        return;
      }

      setSelectedBirdsEyeAccountIds(prev =>
        prev.filter(accountId => !accountIds.includes(accountId))
      );
    },
    [
      accountFilterTab,
      selectedBirdsEyeAccountIds,
      syncMonitoredAccountSelection,
    ]
  );

  const toggleBirdsEyeClusterExpansion = React.useCallback(
    (clusterId: BirdsEyeClusterId) => {
      setExpandedBirdsEyeClusterIds(prev =>
        prev.includes(clusterId)
          ? prev.filter(id => id !== clusterId)
          : [...prev, clusterId]
      );
    },
    []
  );

  // Get user role from localStorage
  const loggedInUser = getFromLocalStorage(STORAGE_KEYS.LOGGED_IN_USER);
  const userRole = loggedInUser?.role;

  const isAccountTeamUser = isAccountTeamRole(userRole);
  const canAccessPortfolio = canAccessMyPortfolio(userRole);
  const canAccessExecutiveMonitored = canAccessExecutiveMonitoredTab(userRole);
  const isExecutive = isRole(userRole, ROLES.EXECUTIVE);

  const formatAssignedOnDate = (dateValue?: string) => {
    if (!dateValue) return "n/a";
    const parsedDate = new Date(dateValue);
    if (Number.isNaN(parsedDate.getTime())) return "n/a";

    return new Intl.DateTimeFormat("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    }).format(parsedDate);
  };

  const renderSponsorCell = (sponsor: string, assignedOn: string) => {
    const sponsorLabel = sponsor || "Unassigned";

    return (
      <div className="min-w-0 text-[13px] font-normal text-[#667085]">
        <span className="flex items-center min-w-0">
          <span
            className="block truncate cursor-help"
            title={`Assigned on: ${assignedOn}`}
            aria-label={`${sponsorLabel}. Assigned on ${assignedOn}`}
          >
            {sponsorLabel}
          </span>
        </span>
      </div>
    );
  };

  const getAccountSidebarMeta = (account: any) => {
    const primarySponsor = String(account.primary_sponsors || "").trim();
    const sponsor = [primarySponsor].filter(Boolean).join(", ");
    const assignedOnRaw = account.assigned_at || account.assigned_on || "n/a";
    const assignedOn = formatAssignedOnDate(assignedOnRaw);
    const hasBriefing = Boolean(account.is_ibbriefing_generated);

    return {
      sponsorLabel: sponsor || "Unassigned",
      assignedOn,
      hasBriefing,
      briefingDate: "Not available",
    };
  };

  const withOrg = useCallback(
    (path: string) => {
      const normalizedPath = path.startsWith("/") ? path : `/${path}`;
      return orgSlug ? `/${orgSlug}${normalizedPath}` : normalizedPath;
    },
    [orgSlug]
  );

  useEffect(() => {
    if (!openAccountMenuKey) return;

    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (accountMenuContainerRef.current?.contains(target)) return;
      const trigger = document.querySelector(
        `[data-account-menu-trigger="${CSS.escape(openAccountMenuKey)}"]`
      );
      if (trigger?.contains(target)) return;
      setOpenAccountMenuKey(null);
      setOpenAccountMenuRect(null);
    };

    const handleCloseOnScroll = () => {
      setOpenAccountMenuKey(null);
      setOpenAccountMenuRect(null);
    };

    document.addEventListener("mousedown", handlePointerDown);
    window.addEventListener("scroll", handleCloseOnScroll, true);
    window.addEventListener("resize", handleCloseOnScroll);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      window.removeEventListener("scroll", handleCloseOnScroll, true);
      window.removeEventListener("resize", handleCloseOnScroll);
    };
  }, [openAccountMenuKey]);

  const toggleAccountRowMenu = useCallback(
    (menuKey: string, event: React.MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();
      event.preventDefault();
      const rect = event.currentTarget.getBoundingClientRect();
      setOpenAccountMenuKey(prev => {
        if (prev === menuKey) {
          setOpenAccountMenuRect(null);
          return null;
        }
        setOpenAccountMenuRect(rect);
        return menuKey;
      });
    },
    []
  );

  const handleOpenAccountSynopsis = useCallback(
    (accountId: string, event: React.MouseEvent) => {
      event.stopPropagation();
      event.preventDefault();
      if (!accountId) return;
      setOpenAccountMenuKey(null);
      setOpenAccountMenuRect(null);
      navigate(withOrg(`/account-synopsis/${accountId}`), {
        state: {
          from: withOrg("/always-on-channel"),
          fromLabel: "Always On Intelligence",
        },
      });
    },
    [navigate, withOrg]
  );

  const handleOpenAccountOverview = useCallback(
    (accountId: string, event: React.MouseEvent) => {
      event.stopPropagation();
      event.preventDefault();
      if (!accountId) return;
      setOpenAccountMenuKey(null);
      setOpenAccountMenuRect(null);
      navigate(withOrg(`/account-workspace/${accountId}`), {
        state: {
          from: withOrg("/always-on-channel"),
          fromLabel: "Always On Intelligence",
        },
      });
    },
    [navigate, withOrg]
  );

  const renderAccountActionsMenu = useCallback(
    (accountId: string, accountName: string, menuKey: string) => {
      if (!accountId) return null;
      const isMenuOpen =
        openAccountMenuKey === menuKey && openAccountMenuRect != null;
      const menuWidth = 210;
      const menuEstimateHeight = 96;
      const openUp =
        isMenuOpen &&
        window.innerHeight - openAccountMenuRect.bottom <
          menuEstimateHeight + 8;
      const menuStyle: React.CSSProperties | undefined = isMenuOpen
        ? {
            position: "fixed",
            top: openUp
              ? openAccountMenuRect.top - menuEstimateHeight - 4
              : openAccountMenuRect.bottom + 4,
            left: Math.min(
              Math.max(8, openAccountMenuRect.right - menuWidth),
              window.innerWidth - menuWidth - 8
            ),
            zIndex: 9999,
            width: menuWidth,
          }
        : undefined;

      return (
        <div className="relative flex justify-center">
          <button
            type="button"
            data-account-menu-trigger={menuKey}
            aria-label={`Actions for ${accountName}`}
            aria-haspopup="menu"
            aria-expanded={isMenuOpen}
            onClick={event => toggleAccountRowMenu(menuKey, event)}
            className="inline-flex h-7 w-7 items-center justify-center rounded-md text-slate-500 transition-colors hover:bg-[#e8eef7] hover:text-slate-700"
          >
            <MoreVertical size={14} strokeWidth={2.25} />
          </button>
          {isMenuOpen &&
            createPortal(
              <div
                ref={accountMenuContainerRef}
                role="menu"
                style={menuStyle}
                className="overflow-hidden rounded-md border border-slate-200 bg-white py-1 shadow-lg"
              >
                <button
                  type="button"
                  role="menuitem"
                  onClick={event => handleOpenAccountSynopsis(accountId, event)}
                  className="block w-full px-3 py-2 text-left text-[12px] font-medium text-slate-700 transition-colors hover:bg-slate-50"
                >
                  Account Synopsis
                </button>
                <button
                  type="button"
                  role="menuitem"
                  onClick={event => handleOpenAccountOverview(accountId, event)}
                  className="block w-full px-3 py-2 text-left text-[12px] font-medium text-slate-700 transition-colors hover:bg-slate-50"
                >
                  Account Workspace
                </button>
              </div>,
              document.body
            )}
        </div>
      );
    },
    [
      handleOpenAccountOverview,
      handleOpenAccountSynopsis,
      openAccountMenuKey,
      openAccountMenuRect,
      toggleAccountRowMenu,
    ]
  );
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const accountTab = (params.get("accountTab") || "").toLowerCase();
    if (accountTab === "monitored") {
      setAccountFilterTab("monitored");
      // Deep-link from "Go to Monitored Accounts" should open Others, not Watchlist.
      const sidebarTab = (params.get("sidebarTab") || "others").toLowerCase();
      const nextSidebarTab: MonitoredSidebarTab =
        sidebarTab === "watchlist" ||
        sidebarTab === "executive" ||
        sidebarTab === "others"
          ? sidebarTab
          : "others";

      const monitoredBox = tabSelectionBoxesRef.current.monitored;
      monitoredBox.activeSidebarTab = nextSidebarTab;
      // Restore this child's saved box; do not wipe sibling child selections.
      const childBox = monitoredBox[nextSidebarTab];
      const childHighlights =
        nextSidebarTab === "executive"
          ? childBox.executiveAccountIds
          : childBox.monitoredAccountIds;

      // URL deep-link wins over first-visit Executive/Others default.
      needsMonitoredDefaultTabResolveRef.current = false;
      visitedAccountFilterTabsRef.current.add("monitored");

      setMonitoredSidebarTab(nextSidebarTab);
      setSelectedMonitoredWatchlistId(childBox.watchlistId);
      setSelectedMonitoredAccountIds([...childBox.monitoredAccountIds]);
      setSelectedExecutiveAccountIds([...childBox.executiveAccountIds]);
      setIsMonitoredAccountSelectionActive(childBox.isAccountSelectionActive);
      setSelectedBirdsEyeAccountIds([...childHighlights]);
    }
  }, [location.search]);

  // Fetch all accounts once (no filtering - get all accounts)
  useEffect(() => {
    dispatch(fetchAccountsWithSponsors({}));
  }, [dispatch]);

  // Business Health legend + Public Data score bubbles
  useEffect(() => {
    void dispatch(fetchHealthScoreThresholds());
  }, [dispatch]);

  useEffect(() => {
    if (accountFilterTab !== "portfolio" || !canAccessPortfolio) return;
    // Sponsors/executives: only primary/secondary assignments.
    // Account-team users: omit role so account_team (and any other) assignments are included.
    dispatch(
      fetchAssignedAccounts({
        sort_by: "name",
        sort_direction: "asc",
        ...(isAccountTeamUser ? {} : { role: "primary,secondary" }),
      })
    );
  }, [dispatch, accountFilterTab, canAccessPortfolio, isAccountTeamUser]);

  useEffect(() => {
    if (accountFilterTab !== "monitored") return;
    dispatch(fetchMonitoredAccounts());
  }, [dispatch, accountFilterTab]);

  useEffect(() => {
    if (accountFilterTab !== "monitored") return;
    if (!canAccessExecutiveMonitored) return;
    dispatch(fetchExecutiveAccounts());
  }, [dispatch, accountFilterTab, canAccessExecutiveMonitored]);

  // First Monitored visit: once executive accounts load, prefer Executive if any exist.
  useEffect(() => {
    if (
      accountFilterTab === "monitored" &&
      canAccessExecutiveMonitored &&
      executiveAccountsLoading
    ) {
      seenExecutiveLoadingOnMonitoredRef.current = true;
    }

    if (!needsMonitoredDefaultTabResolveRef.current) return;
    if (accountFilterTab !== "monitored") return;

    if (!canAccessExecutiveMonitored) {
      needsMonitoredDefaultTabResolveRef.current = false;
      return;
    }

    if (executiveAccountsLoading) return;

    const hasExecutiveAccounts = (executiveAccountsData?.length ?? 0) > 0;
    // Don't resolve empty→Others until we've observed the fetch start (or already have data).
    if (!hasExecutiveAccounts && !seenExecutiveLoadingOnMonitoredRef.current) {
      return;
    }

    const childTab: MonitoredSidebarTab = hasExecutiveAccounts
      ? "executive"
      : "others";

    needsMonitoredDefaultTabResolveRef.current = false;
    seenExecutiveLoadingOnMonitoredRef.current = false;
    const monitoredBox = tabSelectionBoxesRef.current.monitored;
    monitoredBox.activeSidebarTab = childTab;
    const childBox = monitoredBox[childTab];
    const childHighlights =
      childTab === "executive"
        ? childBox.executiveAccountIds
        : childBox.monitoredAccountIds;

    setMonitoredSidebarTab(childTab);
    setSelectedMonitoredWatchlistId(childBox.watchlistId);
    setSelectedMonitoredAccountIds([...childBox.monitoredAccountIds]);
    setSelectedExecutiveAccountIds([...childBox.executiveAccountIds]);
    setIsMonitoredAccountSelectionActive(childBox.isAccountSelectionActive);
    setSelectedBirdsEyeAccountIds([...childHighlights]);
  }, [
    accountFilterTab,
    canAccessExecutiveMonitored,
    executiveAccountsLoading,
    executiveAccountsData,
  ]);

  useEffect(() => {
    if (accountFilterTab === "monitored") {
      dispatch(fetchWatchLists());
    }
  }, [accountFilterTab, dispatch]);

  useEffect(() => {
    if (accountFilterTab !== "monitored") return;
    if (monitoredSidebarTab !== "watchlist") return;

    if (!watchListsData?.length) {
      if (selectedMonitoredWatchlistId !== null) {
        setSelectedMonitoredWatchlistId(null);
      }
      return;
    }

    const selectionStillValid =
      selectedMonitoredWatchlistId != null &&
      watchListsData.some(
        item => item.watch_list_id === selectedMonitoredWatchlistId
      );

    if (!selectionStillValid) {
      setSelectedMonitoredWatchlistId(watchListsData[0].watch_list_id);
    }
  }, [
    accountFilterTab,
    monitoredSidebarTab,
    watchListsData,
    selectedMonitoredWatchlistId,
  ]);

  useEffect(() => {
    if (!pendingCreatedWatchlistName || !watchListsData?.length) return;
    const match = watchListsData.find(
      item =>
        item.watch_list_name?.trim().toLowerCase() ===
        pendingCreatedWatchlistName.trim().toLowerCase()
    );
    if (match) {
      const monitoredBox = tabSelectionBoxesRef.current.monitored;
      monitoredBox.activeSidebarTab = "watchlist";
      monitoredBox.watchlist = {
        watchlistId: match.watch_list_id,
        monitoredAccountIds: [],
        executiveAccountIds: [],
        isAccountSelectionActive: false,
      };
      setMonitoredSidebarTab("watchlist");
      setSelectedMonitoredWatchlistId(match.watch_list_id);
      setSelectedMonitoredAccountIds([]);
      setSelectedExecutiveAccountIds([]);
      setIsMonitoredAccountSelectionActive(false);
      setSelectedBirdsEyeAccountIds([]);
      setPendingCreatedWatchlistName(null);
    }
  }, [watchListsData, pendingCreatedWatchlistName]);

  // Handle account selection for watchlist (portfolio tab) - multiselect
  const handleWatchlistAccountSelection = (account: any) => {
    setSelectedWatchlistCustomerIds(prevState =>
      prevState.includes(account.customer_id)
        ? prevState.filter(id => id !== account.customer_id)
        : [...prevState, account.customer_id]
    );
  };

  const toggleAttentionAccountHighlight = React.useCallback(
    (accountId: string) => {
      if (!accountId) return;
      setHighlightedAttentionAccountIds(prevIds => {
        const nextIds = prevIds.includes(accountId)
          ? prevIds.filter(id => id !== accountId)
          : [...prevIds, accountId];
        if (accountFilterTab !== "monitored") {
          const normalized = normalizeStringArray(nextIds);
          setSelectedBirdsEyeAccountIds(normalized);
          anyPortfolioBirdsEyeRef.current = normalized;
        }
        return nextIds;
      });
    },
    [accountFilterTab]
  );

  const clearAttentionAccountHighlights = React.useCallback(() => {
    setHighlightedAttentionAccountIds([]);
    if (accountFilterTab !== "monitored") {
      setSelectedBirdsEyeAccountIds([]);
      anyPortfolioBirdsEyeRef.current = [];
    }
  }, [accountFilterTab]);

  const handleKpiDataCountsChange = React.useCallback(
    (counts: { available: number; unavailable: number; loading: boolean }) => {
      setKpiDataCounts(prev => {
        if (
          prev.available === counts.available &&
          prev.unavailable === counts.unavailable &&
          prev.loading === counts.loading
        ) {
          return prev;
        }
        return counts;
      });
      // When nothing is unavailable, hide the control and show all KPI rows
      if (!counts.loading && counts.unavailable <= 0) {
        setShowUnavailableKpiData(true);
      }
    },
    []
  );

  const handleAccountsWithoutKpiDataChange = React.useCallback(
    (accountIds: string[]) => {
      setAccountsWithoutKpiData(prev => {
        if (
          prev.length === accountIds.length &&
          prev.every((id, index) => id === accountIds[index])
        ) {
          return prev;
        }
        return accountIds;
      });
    },
    []
  );

  const handleBriefingDataCountsChange = React.useCallback(
    (counts: { available: number; unavailable: number; loading: boolean }) => {
      setBriefingDataCounts(prev => {
        if (
          prev.available === counts.available &&
          prev.unavailable === counts.unavailable &&
          prev.loading === counts.loading
        ) {
          return prev;
        }
        return counts;
      });
      if (!counts.loading && counts.unavailable <= 0) {
        setShowUnavailableBriefingData(true);
      }
    },
    []
  );

  const handleAccountsWithoutBriefingDataChange = React.useCallback(
    (accountIds: string[]) => {
      setAccountsWithoutBriefingData(prev => {
        if (
          prev.length === accountIds.length &&
          prev.every((id, index) => id === accountIds[index])
        ) {
          return prev;
        }
        return accountIds;
      });
    },
    []
  );

  /**
   * Tab-based selection: save the leaving tab's box, restore the next tab's box.
   * Bird's Eye / Attention / Signals (`briefingParentTab`) stays shared.
   * Bird's Eye account picks stay shared across Any ↔ My Portfolio only.
   */
  const handleAccountFilterTabChange = React.useCallback(
    (nextTab: string) => {
      const next = nextTab as AccountFilterTabId;
      if (next !== "any" && next !== "portfolio" && next !== "monitored") {
        return;
      }
      if (next === accountFilterTab) return;

      const boxes = tabSelectionBoxesRef.current;
      const current = accountFilterTab as AccountFilterTabId;

      if (current === "any") {
        boxes.any = {
          highlightedIds: [...highlightedAttentionAccountIds],
          sponsorFilter: { ...selectedSponsorFilter },
        };
        anyPortfolioBirdsEyeRef.current = [...selectedBirdsEyeAccountIds];
      } else if (current === "portfolio") {
        boxes.portfolio = {
          highlightedIds: [...highlightedAttentionAccountIds],
          customerIds: [...selectedWatchlistCustomerIds],
        };
        anyPortfolioBirdsEyeRef.current = [...selectedBirdsEyeAccountIds];
      } else if (current === "monitored") {
        const monitoredBox = boxes.monitored;
        monitoredBox.activeSidebarTab = monitoredSidebarTab;
        monitoredBox[monitoredSidebarTab] = captureMonitoredChildSelection(
          monitoredSidebarTab,
          {
            watchlistId: selectedMonitoredWatchlistId,
            monitoredAccountIds: selectedMonitoredAccountIds,
            executiveAccountIds: selectedExecutiveAccountIds,
            isAccountSelectionActive: isMonitoredAccountSelectionActive,
          }
        );
      }

      visitedAccountFilterTabsRef.current.add(current);

      if (next === "any") {
        const saved = boxes.any;
        setHighlightedAttentionAccountIds(saved.highlightedIds);
        setSelectedSponsorFilter(saved.sponsorFilter);
        setSelectedBirdsEyeAccountIds([...anyPortfolioBirdsEyeRef.current]);
      } else if (next === "portfolio") {
        const saved = boxes.portfolio;
        setHighlightedAttentionAccountIds(saved.highlightedIds);
        if (
          visitedAccountFilterTabsRef.current.has("portfolio") ||
          saved.customerIds.length > 0
        ) {
          setSelectedWatchlistCustomerIds(saved.customerIds);
        }
        setSelectedBirdsEyeAccountIds([...anyPortfolioBirdsEyeRef.current]);
      } else {
        const saved = boxes.monitored;
        const isFirstMonitoredVisit =
          !visitedAccountFilterTabsRef.current.has("monitored");

        let childTab = saved.activeSidebarTab;
        if (isFirstMonitoredVisit) {
          if (!canAccessExecutiveMonitored) {
            needsMonitoredDefaultTabResolveRef.current = false;
            seenExecutiveLoadingOnMonitoredRef.current = false;
            childTab = "others";
          } else if (
            !executiveAccountsLoading &&
            (executiveAccountsData?.length ?? 0) > 0
          ) {
            // Already loaded earlier in the session.
            needsMonitoredDefaultTabResolveRef.current = false;
            seenExecutiveLoadingOnMonitoredRef.current = false;
            childTab = "executive";
          } else {
            // Wait for fetch; land on Others until we know.
            needsMonitoredDefaultTabResolveRef.current = true;
            seenExecutiveLoadingOnMonitoredRef.current =
              executiveAccountsLoading;
            childTab = "others";
          }
          saved.activeSidebarTab = childTab;
        }

        const childBox = saved[childTab];
        const childHighlights =
          childTab === "executive"
            ? childBox.executiveAccountIds
            : childBox.monitoredAccountIds;
        setHighlightedAttentionAccountIds([]);
        setMonitoredSidebarTab(childTab);
        setSelectedMonitoredWatchlistId(childBox.watchlistId);
        setSelectedMonitoredAccountIds([...childBox.monitoredAccountIds]);
        setSelectedExecutiveAccountIds([...childBox.executiveAccountIds]);
        setIsMonitoredAccountSelectionActive(childBox.isAccountSelectionActive);
        setSelectedBirdsEyeAccountIds([...childHighlights]);
      }

      setAccountFilterTab(next);
      setShowUnavailableKpiData(true);
      setKpiDataCounts({ available: 0, unavailable: 0, loading: true });
      setAccountsWithoutKpiData([]);
      setShowUnavailableBriefingData(true);
      setBriefingDataCounts({ available: 0, unavailable: 0, loading: true });
      setAccountsWithoutBriefingData([]);
    },
    [
      accountFilterTab,
      highlightedAttentionAccountIds,
      selectedSponsorFilter,
      selectedBirdsEyeAccountIds,
      selectedWatchlistCustomerIds,
      monitoredSidebarTab,
      selectedMonitoredWatchlistId,
      selectedMonitoredAccountIds,
      selectedExecutiveAccountIds,
      isMonitoredAccountSelectionActive,
      canAccessExecutiveMonitored,
      executiveAccountsLoading,
      executiveAccountsData,
    ]
  );

  const prioritizeAttentionSelections = React.useCallback(
    (accounts: any[]) => {
      if (highlightedAttentionAccountIds.length === 0) return accounts;

      const selectedIds = new Set(highlightedAttentionAccountIds);
      const selectedAccounts = accounts.filter(account =>
        selectedIds.has(String(account.customer_id || account.id || ""))
      );
      const unselectedAccounts = accounts.filter(
        account =>
          !selectedIds.has(String(account.customer_id || account.id || ""))
      );

      return [...selectedAccounts, ...unselectedAccounts];
    },
    [highlightedAttentionAccountIds]
  );

  // Get accounts from Redux store
  const accountsList = accountsData?.items || [];

  const portfolioAccountsList = React.useMemo(() => {
    if (!assignedAccountsItems.length) return [];

    const accountsByCustomerId = new Map(
      accountsList.map((account: any) => [
        String(account.customer_id || account.id || ""),
        account,
      ])
    );

    // Executives' "My Portfolio" only reflects primary/secondary sponsor
    // assignments - extra ("additional") sponsor accounts are excluded here.
    const eligibleAccounts = isExecutive
      ? assignedAccountsItems.filter(
          account =>
            String(account.role_type || "").toLowerCase() !== "additional"
        )
      : assignedAccountsItems;

    return eligibleAccounts.map(account =>
      mapAssignedAccountToDashboardShape(
        account,
        accountsByCustomerId.get(String(account.customer_id))
      )
    );
  }, [assignedAccountsItems, accountsList, isExecutive]);

  const sidebarAccountsList: any[] =
    accountFilterTab === "portfolio" ? portfolioAccountsList : accountsList;

  // Filter accounts based on search query and tab
  const filteredAccounts = React.useMemo(() => {
    const accountsWithoutKpiDataSet = new Set(
      accountsWithoutKpiData.map(id => String(id))
    );
    const isWithoutKpiDataForSort = (account: any) =>
      accountsWithoutKpiDataSet.has(
        String(account?.customer_id || account?.id || account?.account_id || "")
      );

    const filtered = sidebarAccountsList.filter((account: any) =>
      getAccountNameForSort(account)
        .toLowerCase()
        .includes(searchQuery.toLowerCase())
    );

    const isUnassignedForSort = (account: any) => {
      const primarySponsorId = String(account.primary_sponsors_id || "").trim();
      const secondarySponsorIds = account.secondary_sponsors_id
        ? String(account.secondary_sponsors_id)
            .split(",")
            .map((id: string) => id.trim())
            .filter(Boolean)
        : [];
      return !primarySponsorId && secondarySponsorIds.length === 0;
    };

    return [...filtered].sort((left: any, right: any) => {
      if (sortAccountsByKpiAvailability) {
        const leftNoData = isWithoutKpiDataForSort(left);
        const rightNoData = isWithoutKpiDataForSort(right);
        if (leftNoData !== rightNoData) {
          return leftNoData ? 1 : -1;
        }

        const leftUnassigned = isUnassignedForSort(left);
        const rightUnassigned = isUnassignedForSort(right);

        if (leftUnassigned !== rightUnassigned) {
          return leftUnassigned ? -1 : 1;
        }

        const sponsorCompare = getSponsorNameForSort(left).localeCompare(
          getSponsorNameForSort(right),
          undefined,
          { sensitivity: "base" }
        );

        if (sponsorCompare !== 0) {
          return sponsorCompare;
        }
      }

      return getAccountNameForSort(left).localeCompare(
        getAccountNameForSort(right),
        undefined,
        { sensitivity: "base" }
      );
    });
  }, [
    sidebarAccountsList,
    searchQuery,
    accountFilterTab,
    accountsWithoutKpiData,
    sortAccountsByKpiAvailability,
  ]);

  const filteredWatchLists = React.useMemo(() => {
    const lists = watchListsData || [];
    if (!searchQuery.trim()) return lists;
    const query = searchQuery.toLowerCase();
    return lists.filter(
      watchList =>
        watchList.watch_list_name?.toLowerCase().includes(query) ||
        (watchList.customers || []).some(customer =>
          String(customer.name || "")
            .toLowerCase()
            .includes(query)
        )
    );
  }, [watchListsData, searchQuery]);

  const allMonitoredCustomerIds = React.useMemo(
    () =>
      getMonitoredOthersAccountIds(
        monitoredAccountsData || [],
        executiveAccountsData || []
      ),
    [monitoredAccountsData, executiveAccountsData]
  );

  /** Others tab briefing scope — never narrowed by sidebar selection/highlight. */
  const othersBriefingCustomerIds = allMonitoredCustomerIds;

  const allExecutiveCustomerIds = React.useMemo(
    () => getExecutiveAccountIds(executiveAccountsData || []),
    [executiveAccountsData]
  );

  const selectedMonitoredCustomerIds = React.useMemo(() => {
    if (monitoredSidebarTab === "executive") {
      if (!canAccessExecutiveMonitored) return [];
      // Selection is highlight-only — briefing scope stays all executive accounts.
      return allExecutiveCustomerIds;
    }

    if (monitoredSidebarTab === "others") {
      return othersBriefingCustomerIds;
    }

    const lists = watchListsData || [];
    if (!lists.length || !selectedMonitoredWatchlistId) return [];
    const watchList = lists.find(
      item => item.watch_list_id === selectedMonitoredWatchlistId
    );
    return watchList ? getWatchListCustomerIds(watchList, accountsList) : [];
  }, [
    monitoredSidebarTab,
    watchListsData,
    selectedMonitoredWatchlistId,
    accountsList,
    othersBriefingCustomerIds,
    canAccessExecutiveMonitored,
    allExecutiveCustomerIds,
  ]);

  const handleOpenCreateWatchlistModal = React.useCallback(() => {
    setEditWatchListTarget(null);
    setCreateWatchlistInitialIds([]);
    setIsCreateWatchlistModalOpen(true);
  }, []);

  const handleOpenEditWatchlistModal = React.useCallback(
    (watchList: {
      watch_list_id: string;
      watch_list_name: string;
      customers?: Array<{ customer_id?: string; name?: string }>;
    }) => {
      const customerIds = getWatchListCustomerIds(
        watchList as any,
        accountsList
      );
      setEditWatchListTarget({
        watch_list_id: watchList.watch_list_id,
        watch_list_name: watchList.watch_list_name,
        customer_ids: customerIds,
      });
      setCreateWatchlistInitialIds(customerIds);
      setIsCreateWatchlistModalOpen(true);
    },
    [accountsList]
  );

  const handleOpenCreateWatchlistFromPublicData = React.useCallback(
    (accountIds: string[]) => {
      const scopedAccountIds = Array.from(
        new Set(accountIds.map(id => String(id || "").trim()).filter(Boolean))
      );
      if (scopedAccountIds.length === 0) return;
      setEditWatchListTarget(null);
      setCreateWatchlistInitialIds(scopedAccountIds);
      setIsCreateWatchlistModalOpen(true);
    },
    []
  );

  const refreshAfterSaveAccounts = React.useCallback(
    (result: CreateWatchlistModalSuccess) => {
      if (result.type === "watchlist") {
        dispatch(fetchWatchLists());
      }
      if (result.type === "watchlist" && result.watchlistName) {
        setPendingCreatedWatchlistName(result.watchlistName);
      }
    },
    [dispatch]
  );

  const handleSaveAccountsSuccess = React.useCallback(
    (result: CreateWatchlistModalSuccess) => {
      refreshAfterSaveAccounts(result);

      if (accountFilterTab === "monitored") return;

      setSaveAccountsSuccess({
        type: result.type,
        watchlistName: result.watchlistName,
      });
    },
    [accountFilterTab, refreshAfterSaveAccounts]
  );

  const handleGoToMonitoredFromSuccess = React.useCallback(() => {
    const saveType = saveAccountsSuccess?.type ?? "watchlist";
    // Watchlist create → Watchlist child; monitor create → Others child.
    const nextSidebarTab: MonitoredSidebarTab =
      saveType === "monitor" ? "others" : "watchlist";

    setSaveAccountsSuccess(null);
    handleAccountFilterTabChange("monitored");

    const monitoredBox = tabSelectionBoxesRef.current.monitored;
    monitoredBox.activeSidebarTab = nextSidebarTab;
    const childBox = monitoredBox[nextSidebarTab];
    setMonitoredSidebarTab(nextSidebarTab);
    setSelectedMonitoredWatchlistId(childBox.watchlistId);
    setSelectedMonitoredAccountIds([...childBox.monitoredAccountIds]);
    setSelectedExecutiveAccountIds([...childBox.executiveAccountIds]);
    setIsMonitoredAccountSelectionActive(childBox.isAccountSelectionActive);
    setSelectedBirdsEyeAccountIds([...childBox.monitoredAccountIds]);
  }, [handleAccountFilterTabChange, saveAccountsSuccess?.type]);

  const othersBriefingAccounts = React.useMemo(() => {
    if (!othersBriefingCustomerIds.length) return [];
    return accountsList.filter((account: any) =>
      othersBriefingCustomerIds.includes(
        String(account.customer_id || account.id || "")
      )
    );
  }, [accountsList, othersBriefingCustomerIds]);

  const executiveBriefingAccounts = React.useMemo(() => {
    if (!canAccessExecutiveMonitored) return [];
    if (!allExecutiveCustomerIds.length) return [];
    return accountsList.filter((account: any) =>
      allExecutiveCustomerIds.includes(
        String(account.customer_id || account.id || "")
      )
    );
  }, [accountsList, canAccessExecutiveMonitored, allExecutiveCustomerIds]);

  const monitoredBriefingAccounts = React.useMemo(() => {
    if (monitoredSidebarTab === "executive") {
      return executiveBriefingAccounts;
    }

    if (monitoredSidebarTab === "others") {
      return othersBriefingAccounts;
    }

    // Watchlist: briefing scope is the selected watchlist — highlights do not narrow it.
    return accountsForMonitoredBriefing(
      watchListsData || [],
      selectedMonitoredWatchlistId,
      accountsList
    );
  }, [
    monitoredSidebarTab,
    executiveBriefingAccounts,
    othersBriefingAccounts,
    watchListsData,
    selectedMonitoredWatchlistId,
    accountsList,
  ]);

  const handleMonitoredSidebarTabChange = React.useCallback(
    (tab: MonitoredSidebarTab) => {
      if (tab === monitoredSidebarTab) return;

      // User picked a child tab — don't override with first-visit default.
      needsMonitoredDefaultTabResolveRef.current = false;

      const monitoredBox = tabSelectionBoxesRef.current.monitored;

      // Save leaving child's selection box.
      monitoredBox[monitoredSidebarTab] = captureMonitoredChildSelection(
        monitoredSidebarTab,
        {
          watchlistId: selectedMonitoredWatchlistId,
          monitoredAccountIds: selectedMonitoredAccountIds,
          executiveAccountIds: selectedExecutiveAccountIds,
          isAccountSelectionActive: isMonitoredAccountSelectionActive,
        }
      );
      monitoredBox.activeSidebarTab = tab;

      // Restore next child's saved selection box.
      const nextChild = monitoredBox[tab];
      const nextHighlights =
        tab === "executive"
          ? nextChild.executiveAccountIds
          : nextChild.monitoredAccountIds;
      setMonitoredSidebarTab(tab);
      setSelectedMonitoredWatchlistId(nextChild.watchlistId);
      setSelectedMonitoredAccountIds([...nextChild.monitoredAccountIds]);
      setSelectedExecutiveAccountIds([...nextChild.executiveAccountIds]);
      setIsMonitoredAccountSelectionActive(nextChild.isAccountSelectionActive);
      setSelectedBirdsEyeAccountIds([...nextHighlights]);
    },
    [
      monitoredSidebarTab,
      selectedMonitoredWatchlistId,
      selectedMonitoredAccountIds,
      selectedExecutiveAccountIds,
      isMonitoredAccountSelectionActive,
    ]
  );

  const isMonitoredTab = accountFilterTab === "monitored";
  const isPortfolioTab = accountFilterTab === "portfolio";
  const sidebarWidthClass =
    "md:w-[420px] md:min-w-[420px] md:max-w-[420px] md:flex-none";
  const sidebarLoading = isMonitoredTab
    ? watchListsLoading ||
      (monitoredSidebarTab === "others" && monitoredAccountsLoading) ||
      (monitoredSidebarTab === "executive" &&
        canAccessExecutiveMonitored &&
        executiveAccountsLoading)
    : isPortfolioTab
      ? assignedAccountsLoading
      : accountsLoading;
  const showBirdsEyeGroupedAccounts =
    activeTab === "attention" &&
    briefingParentTab === "birds-eye" &&
    (accountFilterTab !== "monitored" ||
      monitoredSidebarTab === "executive" ||
      monitoredSidebarTab === "others");

  const watchlistMetricsInput = React.useMemo<
    HawkeyeMetricsAccountsInput | undefined
  >(() => {
    if (!isMonitoredTab || monitoredSidebarTab !== "watchlist")
      return undefined;
    if (!selectedMonitoredWatchlistId) return undefined;
    const hawkeyeAccounts = monitoredBriefingAccounts.map((account: any) => ({
      id: resolveAccountRecordId(account),
      name: String(account.name || account.account_name || "Unknown Account"),
      city: account.city as string | undefined,
      country: account.country as string | undefined,
      region: account.region as string | undefined,
      latitude: account.latitude as number | undefined,
      longitude: account.longitude as number | undefined,
    }));
    return {
      selectedAccounts: hawkeyeAccounts,
      allWatchlistAccounts: hawkeyeAccounts,
      watchlistId: selectedMonitoredWatchlistId,
      selectedMeasurementDate: null,
      latestMeasurementDate: null,
    };
  }, [
    isMonitoredTab,
    monitoredSidebarTab,
    monitoredBriefingAccounts,
    selectedMonitoredWatchlistId,
  ]);

  const { matrixData, configuration, orderedCategories } = useHawkeyeMetrics(
    watchlistMetricsInput,
    undefined,
    undefined,
    { requireDate: false }
  );

  const accountsById = React.useMemo(
    () =>
      new Map(
        accountsList.map((account: any) => [
          String(account.customer_id || account.id || ""),
          account,
        ])
      ),
    [accountsList]
  );

  const othersAccountCount = allMonitoredCustomerIds.length;
  const executiveAccountCount = allExecutiveCustomerIds.length;

  const filteredExecutiveAccounts = React.useMemo(
    () =>
      mapExecutiveSidebarAccounts(
        executiveAccountsData || [],
        accountsList,
        searchQuery
      ),
    [executiveAccountsData, accountsList, searchQuery]
  );

  const filteredOthersAccounts = React.useMemo(
    () =>
      mapMonitoredOthersSidebarAccounts(
        monitoredAccountsData || [],
        executiveAccountsData || [],
        accountsList,
        searchQuery
      ),
    [monitoredAccountsData, executiveAccountsData, accountsList, searchQuery]
  );

  const monitoredSponsorLabelById = React.useMemo(() => {
    const map = new Map<string, string>();
    filteredExecutiveAccounts.forEach(account => {
      map.set(account.id, account.sponsorLabel || "Unassigned");
    });
    filteredOthersAccounts.forEach(account => {
      map.set(account.id, account.sponsorLabel || "Unassigned");
    });
    return map;
  }, [filteredExecutiveAccounts, filteredOthersAccounts]);

  const createWatchlistPickerAccounts = React.useMemo(
    () =>
      accountsList.map(account => ({
        id: account.customer_id,
        name: account.name || "N/A",
        initials: getInitials(account.name || ""),
        location:
          account.location ||
          account.city ||
          account.region ||
          account.country ||
          "N/A",
        revenue: formatCompactCurrency(account.revenue, { fallback: "N/A" }),
        tier: account.tiers || "N/A",
      })),
    [accountsList]
  );

  // Reset sponsor ID when switching tabs
  useEffect(() => {
    if (accountFilterTab !== "any") {
      setSelectedSponsorFilter({ type: "all" });
    }
  }, [accountFilterTab]);

  /** Monitored sidebar scope is independent from Bird's Eye map compare selection. */
  useEffect(() => {
    if (accountFilterTab !== "monitored") return;
    setSelectedBirdsEyeAccountIds([]);
  }, [accountFilterTab, monitoredSidebarTab, selectedMonitoredWatchlistId]);

  /** Keep shared Any/Portfolio Bird's Eye picks in sync while on those tabs. */
  useEffect(() => {
    if (accountFilterTab === "monitored") return;
    anyPortfolioBirdsEyeRef.current = [...selectedBirdsEyeAccountIds];
  }, [accountFilterTab, selectedBirdsEyeAccountIds]);

  useEffect(() => {
    setSelectedSponsorRoleTab("primary");
  }, [selectedSponsorFilter.type, selectedSponsorFilter.sponsorId]);

  const getPrimarySponsorId = (account: any) =>
    String(account.primary_sponsors_id || "").trim();

  const getSecondarySponsorIds = (account: any) =>
    account.secondary_sponsors_id
      ? String(account.secondary_sponsors_id)
          .split(",")
          .map((id: string) => id.trim())
          .filter(Boolean)
      : [];

  const isUnassignedAccount = (account: any) =>
    !getPrimarySponsorId(account) &&
    getSecondarySponsorIds(account).length === 0;

  const isPrimaryMatch = (account: any) =>
    selectedSponsorFilter.type === "sponsor" &&
    !!selectedSponsorFilter.sponsorId &&
    getPrimarySponsorId(account) === selectedSponsorFilter.sponsorId;

  const isSecondaryMatch = (account: any) =>
    selectedSponsorFilter.type === "sponsor" &&
    !!selectedSponsorFilter.sponsorId &&
    getSecondarySponsorIds(account).includes(selectedSponsorFilter.sponsorId);

  // Account-team users: always a flat list (no Primary / Secondary split).
  const shouldShowRoleTabs =
    !isAccountTeamUser &&
    (isPortfolioTab || selectedSponsorFilter.type === "sponsor");

  const isActivePrimaryMatch = (account: any) => {
    if (isPortfolioTab) {
      return String(account.role_type || "").toLowerCase() === "primary";
    }
    return isPrimaryMatch(account);
  };

  const isActiveSecondaryMatch = (account: any) => {
    if (isPortfolioTab) {
      return String(account.role_type || "").toLowerCase() === "secondary";
    }
    return isSecondaryMatch(account);
  };

  const primaryMatchCount = React.useMemo(
    () =>
      filteredAccounts.filter(account => isActivePrimaryMatch(account)).length,
    [
      filteredAccounts,
      isPortfolioTab,
      selectedSponsorFilter.sponsorId,
      selectedSponsorFilter.type,
    ]
  );

  const secondaryMatchCount = React.useMemo(
    () =>
      filteredAccounts.filter(account => isActiveSecondaryMatch(account))
        .length,
    [
      filteredAccounts,
      isPortfolioTab,
      selectedSponsorFilter.sponsorId,
      selectedSponsorFilter.type,
    ]
  );

  const showingCount = React.useMemo(() => {
    if (isAccountTeamUser) {
      if (selectedSponsorFilter.type === "unassigned") {
        return filteredAccounts.filter(account => isUnassignedAccount(account))
          .length;
      }
      if (selectedSponsorFilter.type === "sponsor") {
        return filteredAccounts.filter(
          account => isPrimaryMatch(account) || isSecondaryMatch(account)
        ).length;
      }
      return filteredAccounts.length;
    }
    if (accountFilterTab === "portfolio") {
      return selectedSponsorRoleTab === "primary"
        ? primaryMatchCount
        : secondaryMatchCount;
    }
    if (selectedSponsorFilter.type === "all") return filteredAccounts.length;
    if (selectedSponsorFilter.type === "unassigned") {
      return filteredAccounts.filter(account => isUnassignedAccount(account))
        .length;
    }
    return primaryMatchCount + secondaryMatchCount;
  }, [
    filteredAccounts,
    accountFilterTab,
    isAccountTeamUser,
    selectedSponsorFilter.type,
    selectedSponsorFilter.sponsorId,
    selectedSponsorRoleTab,
    primaryMatchCount,
    secondaryMatchCount,
  ]);

  const accountsWithoutKpiDataSet = React.useMemo(
    () => new Set(accountsWithoutKpiData.map(id => String(id))),
    [accountsWithoutKpiData]
  );

  const isWithoutKpiData = React.useCallback(
    (account: any) =>
      accountsWithoutKpiDataSet.has(
        String(account?.customer_id || account?.id || account?.account_id || "")
      ),
    [accountsWithoutKpiDataSet]
  );

  const accountsWithoutBriefingDataSet = React.useMemo(
    () => new Set(accountsWithoutBriefingData.map(id => String(id))),
    [accountsWithoutBriefingData]
  );

  const isWithoutBriefingData = React.useCallback(
    (account: any) =>
      accountsWithoutBriefingDataSet.has(
        String(account?.customer_id || account?.id || account?.account_id || "")
      ),
    [accountsWithoutBriefingDataSet]
  );

  const isWithoutGroupedData = React.useCallback(
    (account: any) =>
      sortAccountsByKpiAvailability
        ? isWithoutKpiData(account)
        : isWithoutBriefingData(account),
    [sortAccountsByKpiAvailability, isWithoutKpiData, isWithoutBriefingData]
  );

  /** Purple top block + divider. KPI and briefing toggles both group this way. */
  const useAvailabilityGrouping =
    (sortAccountsByKpiAvailability || groupAccountsByBriefingAvailability) &&
    selectedSponsorFilter.type === "all" &&
    !shouldShowRoleTabs;

  const topBlockAccounts = React.useMemo(() => {
    // KPI toggle on + All Accounts: group accounts with data in the purple top block.
    if (useAvailabilityGrouping) {
      return prioritizeAttentionSelections(
        filteredAccounts.filter(account => !isWithoutGroupedData(account))
      );
    }
    // Account-team: single flat list (no primary/secondary highlight block).
    if (isAccountTeamUser) return [];
    if (selectedSponsorFilter.type === "unassigned") {
      return prioritizeAttentionSelections(
        filteredAccounts.filter(account => isUnassignedAccount(account))
      );
    }
    if (shouldShowRoleTabs) {
      return prioritizeAttentionSelections(
        filteredAccounts.filter(account =>
          selectedSponsorRoleTab === "primary"
            ? isActivePrimaryMatch(account)
            : isActiveSecondaryMatch(account)
        )
      );
    }
    return [];
  }, [
    filteredAccounts,
    selectedSponsorFilter,
    selectedSponsorRoleTab,
    isAccountTeamUser,
    shouldShowRoleTabs,
    prioritizeAttentionSelections,
    useAvailabilityGrouping,
    isWithoutGroupedData,
  ]);

  const remainingAccounts = React.useMemo(() => {
    // KPI toggle on + All Accounts: accounts without data sit below the divider.
    if (useAvailabilityGrouping) {
      return prioritizeAttentionSelections(
        filteredAccounts.filter(account => isWithoutGroupedData(account))
      );
    }
    // Account-team: flat list (optionally filtered by selected sponsor / unassigned).
    if (isAccountTeamUser) {
      if (selectedSponsorFilter.type === "unassigned") {
        return prioritizeAttentionSelections(
          filteredAccounts.filter(account => isUnassignedAccount(account))
        );
      }
      if (selectedSponsorFilter.type === "sponsor") {
        return prioritizeAttentionSelections(
          filteredAccounts.filter(
            account => isPrimaryMatch(account) || isSecondaryMatch(account)
          )
        );
      }
      return prioritizeAttentionSelections(filteredAccounts);
    }
    if (selectedSponsorFilter.type === "all" && !shouldShowRoleTabs)
      return prioritizeAttentionSelections(filteredAccounts);
    // Unassigned filter: only the top block — do not list assigned accounts below.
    if (selectedSponsorFilter.type === "unassigned") {
      return [];
    }
    // Specific sponsor: top = active Primary/Secondary; below = the other role for that sponsor only.
    if (selectedSponsorFilter.type === "sponsor") {
      return prioritizeAttentionSelections(
        filteredAccounts.filter(account =>
          selectedSponsorRoleTab === "primary"
            ? isActiveSecondaryMatch(account)
            : isActivePrimaryMatch(account)
        )
      );
    }
    if (shouldShowRoleTabs) {
      return prioritizeAttentionSelections(
        filteredAccounts.filter(account =>
          selectedSponsorRoleTab === "primary"
            ? !isActivePrimaryMatch(account)
            : !isActiveSecondaryMatch(account)
        )
      );
    }
    return prioritizeAttentionSelections(
      filteredAccounts.filter(account =>
        selectedSponsorRoleTab === "primary"
          ? !isPrimaryMatch(account)
          : !isSecondaryMatch(account)
      )
    );
  }, [
    filteredAccounts,
    selectedSponsorFilter,
    selectedSponsorRoleTab,
    isAccountTeamUser,
    shouldShowRoleTabs,
    prioritizeAttentionSelections,
    useAvailabilityGrouping,
    isWithoutGroupedData,
  ]);

  const filteredBirdsEyeClusterGroups = React.useMemo(() => {
    const visibleAccountIds = new Set(
      isMonitoredTab &&
      (monitoredSidebarTab === "executive" || monitoredSidebarTab === "others")
        ? (monitoredSidebarTab === "executive"
            ? filteredExecutiveAccounts
            : filteredOthersAccounts
          ).map(account => account.id)
        : filteredAccounts.map((account: any) =>
            String(account.customer_id || account.id || "")
          )
    );

    return BIRDS_EYE_CLUSTER_ORDER.map(clusterId => {
      const group = birdsEyeClusterGroups.find(
        clusterGroup => clusterGroup.clusterId === clusterId
      );
      const fallbackMeta = BIRDS_EYE_CLUSTER_FALLBACK_META[clusterId];
      if (!group) {
        return {
          clusterId,
          label: fallbackMeta.label,
          accentColor: fallbackMeta.accentColor,
          textColor: fallbackMeta.textColor,
          count: 0,
          avg: 0,
          accounts: [],
        } as BirdsEyeClusterSummary;
      }

      const accounts = group.accounts
        .filter(account => visibleAccountIds.has(account.accountId))
        .sort((left, right) => {
          const leftSelectedIndex = selectedBirdsEyeAccountIds.indexOf(
            left.accountId
          );
          const rightSelectedIndex = selectedBirdsEyeAccountIds.indexOf(
            right.accountId
          );
          const leftSelected = leftSelectedIndex !== -1;
          const rightSelected = rightSelectedIndex !== -1;

          if (leftSelected !== rightSelected) {
            return leftSelected ? -1 : 1;
          }

          if (leftSelected && rightSelected) {
            return leftSelectedIndex - rightSelectedIndex;
          }

          const leftSourceAccount = accountsById.get(left.accountId);
          const rightSourceAccount = accountsById.get(right.accountId);
          const leftUnassigned = leftSourceAccount
            ? isUnassignedAccount(leftSourceAccount)
            : false;
          const rightUnassigned = rightSourceAccount
            ? isUnassignedAccount(rightSourceAccount)
            : false;

          if (leftUnassigned !== rightUnassigned) {
            return leftUnassigned ? -1 : 1;
          }

          const sponsorCompare = getSponsorNameForSort(
            leftSourceAccount
          ).localeCompare(
            getSponsorNameForSort(rightSourceAccount),
            undefined,
            {
              sensitivity: "base",
            }
          );

          if (sponsorCompare !== 0) {
            return sponsorCompare;
          }

          return left.accountName.localeCompare(right.accountName, undefined, {
            sensitivity: "base",
          });
        });
      const avg = accounts.length
        ? Math.round(
            accounts.reduce((sum, account) => sum + account.overall, 0) /
              accounts.length
          )
        : 0;

      return {
        ...group,
        count: accounts.length,
        avg,
        accounts,
      };
    });
  }, [
    birdsEyeClusterGroups,
    filteredAccounts,
    accountsById,
    selectedBirdsEyeAccountIds,
    isMonitoredTab,
    monitoredSidebarTab,
    filteredExecutiveAccounts,
    filteredOthersAccounts,
  ]);

  const birdsEyeGroupedAccountCount = React.useMemo(
    () =>
      filteredBirdsEyeClusterGroups.reduce(
        (sum, group) =>
          group.clusterId === "kpi-data-not-available"
            ? sum
            : sum + group.count,
        0
      ),
    [filteredBirdsEyeClusterGroups]
  );

  /**
   * Bird's Eye sidebar expansion:
   * - With selections → open every cluster that has a selected account
   * - With no selections → open the first cluster only
   */
  useEffect(() => {
    if (!showBirdsEyeGroupedAccounts) return;
    if (filteredBirdsEyeClusterGroups.length === 0) return;

    if (selectedBirdsEyeAccountIds.length === 0) {
      setExpandedBirdsEyeClusterIds(prev => {
        const firstClusterId = BIRDS_EYE_CLUSTER_ORDER[0];
        if (prev.length === 1 && prev[0] === firstClusterId) return prev;
        return [firstClusterId];
      });
      return;
    }

    const selectedIdSet = new Set(selectedBirdsEyeAccountIds);
    const clustersWithSelection = BIRDS_EYE_CLUSTER_ORDER.filter(clusterId => {
      const group = filteredBirdsEyeClusterGroups.find(
        clusterGroup => clusterGroup.clusterId === clusterId
      );
      return group?.accounts.some(account =>
        selectedIdSet.has(account.accountId)
      );
    });

    if (clustersWithSelection.length === 0) {
      setExpandedBirdsEyeClusterIds(prev => {
        const firstClusterId = BIRDS_EYE_CLUSTER_ORDER[0];
        if (prev.length === 1 && prev[0] === firstClusterId) return prev;
        return [firstClusterId];
      });
      return;
    }

    setExpandedBirdsEyeClusterIds(prev =>
      areStringArraysEqual(prev, clustersWithSelection)
        ? prev
        : clustersWithSelection
    );
  }, [
    filteredBirdsEyeClusterGroups,
    selectedBirdsEyeAccountIds,
    showBirdsEyeGroupedAccounts,
  ]);

  // Set default selections for portfolio tab when assigned accounts load
  useEffect(() => {
    if (
      isPortfolioTab &&
      canAccessPortfolio &&
      portfolioAccountsList.length > 0 &&
      selectedWatchlistCustomerIds.length === 0
    ) {
      const allCustomerIds = portfolioAccountsList.map(
        (account: any) => account.customer_id
      );
      setSelectedWatchlistCustomerIds(allCustomerIds);
    }
  }, [
    isPortfolioTab,
    canAccessPortfolio,
    portfolioAccountsList,
    selectedWatchlistCustomerIds.length,
  ]);

  return (
    <div className="dashboard flex flex-col">
      <div className="flex flex-1 flex-wrap -mx-2">
        <div className="flex flex-col px-2 w-full">
          {activeTab === "attention" &&
            (accountFilterTab === "any" ||
              accountFilterTab === "portfolio" ||
              accountFilterTab === "monitored") && (
              <div className="mb-4 shrink-0 space-y-3">
                <div className="rounded-lg border border-gray-200 bg-white overflow-hidden shadow-sm">
                  <AccountBriefingLegend />
                </div>
              </div>
            )}
          <div
            className={`sticky top-0 z-10 flex min-h-0 items-stretch gap-5 bg-gray-100 ${ATTENTION_COLUMNS_H}`}
          >
            <div
              className={`flex h-full min-h-0 w-full flex-col self-stretch overflow-hidden ${ATTENTION_COLUMNS_H} ${sidebarWidthClass} sidebar rounded-lg border border-gray-200 bg-white shadow-sm`}
            >
              <div className="shrink-0 sticky top-0 z-5 bg-white/95 backdrop-blur px-3 pt-3.5 pb-3 border-b border-gray-100">
                <div className="accounts-header flex items-center justify-between mb-3">
                  <h1 className="section-title mb-0">Accounts</h1>
                </div>

                <div className="mb-3 dashboard__filter-dropdown">
                  <TabsComponent
                    tabs={[
                      {
                        id: "any",
                        label: "Any Portfolio",
                        icon: <Globe2 size={14} strokeWidth={1.75} />,
                      },
                      {
                        id: "portfolio",
                        label: "My Portfolio",
                        icon: <UserRound size={14} strokeWidth={1.75} />,
                      },
                      {
                        id: "monitored",
                        label: "Monitored",
                        icon: <Eye size={14} strokeWidth={1.75} />,
                      },
                    ]}
                    activeTab={accountFilterTab}
                    onTabChange={handleAccountFilterTabChange}
                    variant="secondary-segmented"
                    className="rounded-full"
                  />
                </div>

                <div className="mt-3 monitored-accounts__search">
                  <SearchInput
                    placeholder={
                      isMonitoredTab
                        ? "Search Watchlist or Account"
                        : "Search Account"
                    }
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                  />
                </div>
              </div>

              <div className="account-list flex min-h-0 flex-1 flex-col overflow-hidden p-3">
                {sidebarLoading ? (
                  <div className="text-center mt-3">
                    <div className="flex justify-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
                    </div>
                    <p className="mb-2">
                      {isMonitoredTab
                        ? "Loading watch lists..."
                        : "Loading accounts data..."}
                    </p>
                  </div>
                ) : accountFilterTab === "monitored" &&
                  !showBirdsEyeGroupedAccounts ? (
                  <div
                    ref={scrollChainRef}
                    className="scrollbar-hover min-h-0 flex-1 overflow-y-auto overscroll-contain"
                  >
                    <MonitoredWatchlistSidebar
                      activeTab={monitoredSidebarTab}
                      onTabChange={handleMonitoredSidebarTabChange}
                      canAccessExecutiveTab={canAccessExecutiveMonitored}
                      filteredWatchLists={filteredWatchLists}
                      accountsList={accountsList}
                      executiveAccounts={filteredExecutiveAccounts}
                      executiveAccountCount={executiveAccountCount}
                      executiveAccountsLoading={executiveAccountsLoading}
                      executiveAccountsError={executiveAccountsError}
                      selectedExecutiveAccountIds={selectedExecutiveAccountIds}
                      othersAccounts={filteredOthersAccounts}
                      othersAccountCount={othersAccountCount}
                      othersAccountsLoading={monitoredAccountsLoading}
                      othersAccountsError={monitorAccountsError}
                      selectedMonitoredAccountIds={selectedMonitoredAccountIds}
                      selectedWatchlistId={selectedMonitoredWatchlistId}
                      matrixData={matrixData}
                      configuration={configuration}
                      orderedCategories={orderedCategories}
                      mapHighlightedAccountIds={
                        briefingParentTab === "birds-eye"
                          ? selectedBirdsEyeAccountIds
                          : []
                      }
                      birdsEyeMode={briefingParentTab === "birds-eye"}
                      birdsEyeClusterGroups={birdsEyeClusterGroups}
                      showUnavailableKpiData={sortAccountsByKpiAvailability}
                      onSelectWatchlist={watchlistId => {
                        setIsMonitoredAccountSelectionActive(false);
                        setSelectedMonitoredAccountIds([]);
                        setSelectedExecutiveAccountIds([]);
                        setSelectedMonitoredWatchlistId(watchlistId);
                        setSelectedBirdsEyeAccountIds([]);
                      }}
                      onSelectExecutiveAccount={accountId => {
                        // Executive tab: highlight/select only — do not change briefing scope.
                        const next = selectedExecutiveAccountIds.includes(
                          accountId
                        )
                          ? selectedExecutiveAccountIds.filter(
                              id => id !== accountId
                            )
                          : [...selectedExecutiveAccountIds, accountId];
                        syncMonitoredAccountSelection(next);
                      }}
                      onSelectMonitoredAccount={accountId => {
                        // Others tab: highlight/select only — do not change briefing scope.
                        const next = selectedMonitoredAccountIds.includes(
                          accountId
                        )
                          ? selectedMonitoredAccountIds.filter(
                              id => id !== accountId
                            )
                          : [...selectedMonitoredAccountIds, accountId];
                        syncMonitoredAccountSelection(next);
                      }}
                      onSwitchToAnyPortfolio={() =>
                        handleAccountFilterTabChange("any")
                      }
                      onCreateWatchlist={handleOpenCreateWatchlistModal}
                      onEditWatchlist={handleOpenEditWatchlistModal}
                      onWatchlistDeleted={watchlistId => {
                        if (selectedMonitoredWatchlistId === watchlistId) {
                          setSelectedMonitoredWatchlistId(null);
                        }
                      }}
                      onMonitoredAccountDeleted={accountId => {
                        setSelectedMonitoredAccountIds(prevIds =>
                          prevIds.filter(id => id !== accountId)
                        );
                      }}
                    />
                  </div>
                ) : isPortfolioTab &&
                  !canAccessPortfolio &&
                  !showBirdsEyeGroupedAccounts ? (
                  <PortfolioSponsorOnlyState
                    variant="sidebar"
                    onSwitchToAny={() => handleAccountFilterTabChange("any")}
                  />
                ) : showBirdsEyeGroupedAccounts ||
                  filteredAccounts.length > 0 ? (
                  <div className="min-h-0 flex-1 overflow-hidden rounded-xl pt-0.5 flex flex-col">
                    {isMonitoredTab && showBirdsEyeGroupedAccounts && (
                      <div className="mb-3 shrink-0 grid grid-cols-3 overflow-visible rounded-md border border-[#d1d5db] bg-white text-[13px] font-medium">
                        {(
                          [
                            {
                              id: "executive" as const,
                              label: "Executive",
                              count: executiveAccountCount,
                            },
                            {
                              id: "watchlist" as const,
                              label: "Watchlist",
                              count: filteredWatchLists.length,
                            },
                            {
                              id: "others" as const,
                              label: "Others",
                              count: othersAccountCount,
                            },
                          ] as const
                        ).map((tab, index) => {
                          const isActive = monitoredSidebarTab === tab.id;
                          const isFirst = index === 0;
                          const isLast = index === 2;
                          return (
                            <button
                              key={tab.id}
                              type="button"
                              onClick={() =>
                                handleMonitoredSidebarTabChange(tab.id)
                              }
                              className={`px-1 py-2.5 transition-colors ${
                                isFirst ? "rounded-l-[5px]" : ""
                              } ${isLast ? "rounded-r-[5px]" : ""} ${
                                isActive
                                  ? "bg-[#f5edfc] font-semibold text-[#974ae4]"
                                  : "bg-white text-[#4b5563] hover:bg-[#f3f4f6]"
                              }`}
                            >
                              {tab.label} ({tab.count})
                            </button>
                          );
                        })}
                      </div>
                    )}
                    <div className="shrink-0 bg-blue-50 p-3 rounded-lg">
                      <div className="flex items-center justify-between text-[13px] font-semibold text-[#0d4a8c]">
                        <div className="inline-flex items-center gap-1.5">
                          <span className="font-semibold text-[#0d4a8c]">
                            All Accounts
                          </span>
                          <span className="text-[#1b4f9a]">
                            {showBirdsEyeGroupedAccounts && isMonitoredTab
                              ? birdsEyeGroupedAccountCount
                              : filteredAccounts.length}
                          </span>
                        </div>
                        <div className="inline-flex items-center gap-2 text-[#2271cc]">
                          {accountFilterTab !== "portfolio" &&
                            !isMonitoredTab && (
                              <>
                                <span className="font-semibold">Showing</span>
                                <span className="inline-flex items-center rounded-md border border-[#9ec1ea] bg-[#d3e8ff] px-3 py-1 text-[#0f5aac] font-semibold">
                                  {selectedSponsorFilter.type === "all"
                                    ? "All"
                                    : selectedSponsorFilter.type ===
                                        "unassigned"
                                      ? "Unassigned"
                                      : selectedSponsorFilter.sponsorName ||
                                        "Selected"}
                                  <span className="ml-2">{showingCount}</span>
                                </span>
                              </>
                            )}
                          {isMonitoredTab && showBirdsEyeGroupedAccounts && (
                            <span className="inline-flex items-center rounded-md border border-[#9ec1ea] bg-[#d3e8ff] px-3 py-1 text-[#0f5aac] font-semibold">
                              Showing All {birdsEyeGroupedAccountCount}
                            </span>
                          )}
                          {(isMonitoredTab
                            ? selectedBirdsEyeAccountIds.length > 0
                            : highlightedAttentionAccountIds.length > 0) && (
                            <button
                              type="button"
                              onClick={() => {
                                if (isMonitoredTab) {
                                  syncMonitoredAccountSelection([]);
                                  return;
                                }
                                clearAttentionAccountHighlights();
                              }}
                              className="text-[12px] font-semibold text-slate-500 transition-colors hover:text-slate-700"
                            >
                              Clear
                            </button>
                          )}
                        </div>
                      </div>
                      {shouldShowRoleTabs && !showBirdsEyeGroupedAccounts && (
                        <div className="mt-2 grid grid-cols-2 rounded-md overflow-hidden border border-blue-200 bg-[#eef6ff] text-[13px] font-medium">
                          <button
                            type="button"
                            onClick={() => setSelectedSponsorRoleTab("primary")}
                            className={`py-1.5 ${
                              selectedSponsorRoleTab === "primary"
                                ? "text-blue-700 bg-white"
                                : "text-blue-500"
                            }`}
                          >
                            Primary ({primaryMatchCount})
                          </button>
                          <button
                            type="button"
                            onClick={() =>
                              setSelectedSponsorRoleTab("secondary")
                            }
                            className={`py-1.5 border-l border-blue-200 ${
                              selectedSponsorRoleTab === "secondary"
                                ? "text-blue-700 bg-white"
                                : "text-blue-500"
                            }`}
                          >
                            Secondary ({secondaryMatchCount})
                          </button>
                        </div>
                      )}
                    </div>
                    {showBirdsEyeGroupedAccounts ? (
                      <div
                        ref={scrollChainRef}
                        className="scrollbar-hover min-h-0 flex-1 overflow-y-auto overscroll-contain"
                      >
                        <div className="flex flex-col space-y-5 px-0 py-3">
                          {birdsEyeClusterGroups.length === 0 ? (
                            <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-3 text-center text-sm text-slate-500">
                              Loading account groups...
                            </div>
                          ) : (
                            filteredBirdsEyeClusterGroups.flatMap(group => {
                              const groupAccountIds = group.accounts.map(
                                clusterAccount => clusterAccount.accountId
                              );
                              const isKpiDataUnavailableGroup =
                                group.clusterId === "kpi-data-not-available";
                              const isGroupExpanded =
                                expandedBirdsEyeClusterIds.includes(
                                  group.clusterId
                                );
                              const isGroupSelected =
                                !isKpiDataUnavailableGroup &&
                                groupAccountIds.length > 0 &&
                                groupAccountIds.every(accountId =>
                                  selectedBirdsEyeAccountIds.includes(accountId)
                                );
                              const hasSelectedGroupAccounts =
                                !isKpiDataUnavailableGroup &&
                                groupAccountIds.some(accountId =>
                                  selectedBirdsEyeAccountIds.includes(accountId)
                                );

                              const groupNode = (
                                <div
                                  key={group.clusterId}
                                  className="flex flex-col space-y-1"
                                >
                                  <div
                                    role="button"
                                    tabIndex={0}
                                    onClick={() =>
                                      toggleBirdsEyeClusterExpansion(
                                        group.clusterId
                                      )
                                    }
                                    onKeyDown={event => {
                                      if (
                                        event.key === "Enter" ||
                                        event.key === " "
                                      ) {
                                        event.preventDefault();
                                        toggleBirdsEyeClusterExpansion(
                                          group.clusterId
                                        );
                                      }
                                    }}
                                    className="flex items-center justify-between gap-2 px-2 py-2"
                                  >
                                    <div className="flex min-w-0 flex-1 items-center gap-2.5 text-left">
                                      {!isKpiDataUnavailableGroup && (
                                        <>
                                          <button
                                            type="button"
                                            role="checkbox"
                                            aria-checked={isGroupSelected}
                                            onClick={event => {
                                              event.stopPropagation();
                                              toggleBirdsEyeGroupSelection(
                                                groupAccountIds
                                              );
                                            }}
                                            onMouseDown={event =>
                                              event.stopPropagation()
                                            }
                                            disabled={
                                              group.accounts.length === 0
                                            }
                                            aria-label={`Select all ${group.label} accounts`}
                                            className={`inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-[4px] border transition-colors ${
                                              isGroupSelected
                                                ? "border-violet-600 bg-violet-600 text-white"
                                                : "border-gray-400 bg-white text-transparent hover:border-gray-500"
                                            } disabled:cursor-not-allowed disabled:opacity-50`}
                                          >
                                            <Check className="h-3 w-3 stroke-[3]" />
                                          </button>
                                          <span
                                            className="h-2.5 w-2.5 shrink-0 rounded-full"
                                            style={{
                                              backgroundColor:
                                                group.accentColor,
                                            }}
                                          />
                                        </>
                                      )}
                                      {isKpiDataUnavailableGroup ? (
                                        <span className="inline-flex min-w-0 max-w-full items-center gap-1.5">
                                          <span
                                            className="min-w-0 truncate text-[13px] font-medium uppercase tracking-[0.03em]"
                                            style={{ color: group.textColor }}
                                          >
                                            <span className="normal-case">
                                              KPIs
                                            </span>{" "}
                                            DATA NOT AVAILABLE
                                          </span>
                                          <KpiDataUnavailableBanner />
                                        </span>
                                      ) : (
                                        <span
                                          className="min-w-0 truncate text-[13px] font-medium uppercase tracking-[0.03em]"
                                          style={{ color: group.textColor }}
                                        >
                                          {group.label}
                                        </span>
                                      )}
                                    </div>
                                    <div className="flex shrink-0 items-center gap-2">
                                      {hasSelectedGroupAccounts && (
                                        <button
                                          type="button"
                                          onClick={event => {
                                            event.stopPropagation();
                                            clearBirdsEyeGroupSelection(
                                              groupAccountIds
                                            );
                                          }}
                                          className="text-[11px] font-semibold text-slate-500 transition-colors hover:text-slate-700"
                                        >
                                          Clear
                                        </button>
                                      )}
                                      <span
                                        className="inline-flex min-w-[1.25rem] items-center justify-center rounded-full px-1.5 py-0.5 text-[10px] font-bold"
                                        style={{
                                          color: group.textColor,
                                          backgroundColor: `${group.accentColor}20`,
                                        }}
                                      >
                                        {group.count}
                                      </span>
                                      {isGroupExpanded ? (
                                        <ChevronDown className="h-4 w-4 shrink-0 text-slate-500" />
                                      ) : (
                                        <ChevronRight className="h-4 w-4 shrink-0 text-slate-500" />
                                      )}
                                    </div>
                                  </div>
                                  {isGroupExpanded ? (
                                    <div className="rounded-lg border border-[#e5e9ef] bg-white px-2 py-1.5">
                                      {group.accounts.length > 0 ? (
                                        group.accounts.map(clusterAccount => {
                                          const sourceAccount =
                                            accountsById.get(
                                              clusterAccount.accountId
                                            );
                                          const accountMeta = sourceAccount
                                            ? getAccountSidebarMeta(
                                                sourceAccount
                                              )
                                            : {
                                                sponsorLabel: "Unassigned",
                                                assignedOn: "n/a",
                                                hasBriefing: false,
                                                briefingDate: "Not available",
                                              };
                                          if (
                                            accountMeta.sponsorLabel ===
                                              "Unassigned" &&
                                            monitoredSponsorLabelById.has(
                                              clusterAccount.accountId
                                            )
                                          ) {
                                            accountMeta.sponsorLabel =
                                              monitoredSponsorLabelById.get(
                                                clusterAccount.accountId
                                              ) || "Unassigned";
                                          }

                                          return (
                                            <div
                                              key={clusterAccount.accountId}
                                              className={`mb-1 grid grid-cols-[1.45fr_1.2fr_0.45fr] items-center gap-2 rounded-md px-2 py-2 transition-colors ${
                                                !isKpiDataUnavailableGroup &&
                                                selectedBirdsEyeAccountIds.includes(
                                                  clusterAccount.accountId
                                                )
                                                  ? "bg-violet-50 ring-1 ring-inset ring-violet-300"
                                                  : "bg-[#f5f5fa]"
                                              } ${
                                                isKpiDataUnavailableGroup
                                                  ? "cursor-not-allowed opacity-70"
                                                  : "cursor-pointer hover:bg-gray-100"
                                              }`}
                                              style={{
                                                cursor:
                                                  isKpiDataUnavailableGroup
                                                    ? "not-allowed"
                                                    : "pointer",
                                              }}
                                              title={
                                                isKpiDataUnavailableGroup
                                                  ? "Accounts without KPIs data cannot be selected"
                                                  : undefined
                                              }
                                              onClick={() => {
                                                if (isKpiDataUnavailableGroup) {
                                                  return;
                                                }
                                                const next =
                                                  selectedBirdsEyeAccountIds.includes(
                                                    clusterAccount.accountId
                                                  )
                                                    ? selectedBirdsEyeAccountIds.filter(
                                                        id =>
                                                          id !==
                                                          clusterAccount.accountId
                                                      )
                                                    : normalizeStringArray([
                                                        ...selectedBirdsEyeAccountIds,
                                                        clusterAccount.accountId,
                                                      ]);
                                                if (isMonitoredTab) {
                                                  syncMonitoredAccountSelection(
                                                    next
                                                  );
                                                  return;
                                                }
                                                setSelectedBirdsEyeAccountIds(
                                                  next
                                                );
                                              }}
                                            >
                                              <div className="min-w-0 text-[13px] font-medium text-[#111827]">
                                                <span className="truncate block">
                                                  {clusterAccount.accountName}
                                                </span>
                                              </div>
                                              {renderSponsorCell(
                                                accountMeta.sponsorLabel,
                                                accountMeta.assignedOn
                                              )}
                                              {renderAccountActionsMenu(
                                                clusterAccount.accountId,
                                                clusterAccount.accountName,
                                                `birds-eye:${clusterAccount.accountId}`
                                              )}
                                            </div>
                                          );
                                        })
                                      ) : (
                                        <div className="px-2 py-2 text-[11px] text-slate-400">
                                          No accounts in this group.
                                        </div>
                                      )}
                                    </div>
                                  ) : null}
                                </div>
                              );

                              if (isKpiDataUnavailableGroup) {
                                if (group.count <= 0) {
                                  return [];
                                }
                                return [
                                  <div
                                    key="kpi-unavailable-section"
                                    className="flex flex-col gap-1.5"
                                  >
                                    <div
                                      className="border-t border-gray-300 mx-2"
                                      role="separator"
                                    />
                                    {groupNode}
                                  </div>,
                                ];
                              }

                              return [groupNode];
                            })
                          )}
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className="shrink-0 grid grid-cols-[1.45fr_1.2fr_0.45fr] gap-2 border-b border-[#dedee6] bg-white px-2 pb-2 pt-3 text-[11px] font-semibold text-[#384a63]">
                          <span className="min-w-0">Account Name</span>
                          <span className="min-w-0">Primary Sponsor</span>
                          <span className="text-center min-w-0">Action</span>
                        </div>
                        <div
                          ref={scrollChainRef}
                          className="scrollbar-hover min-h-0 flex-1 overflow-y-auto overscroll-contain"
                        >
                          <div className="flex flex-col gap-1.5 px-0 py-2.5">
                            {topBlockAccounts.length > 0 && (
                              <div className="rounded-lg border border-[#e0cdf7] bg-[#f5f5fa] overflow-hidden px-3 py-1">
                                {topBlockAccounts.map((account: any) => {
                                  const primarySponsor = String(
                                    account.primary_sponsors || ""
                                  ).trim();
                                  const sponsor = [primarySponsor]
                                    .filter(Boolean)
                                    .join(", ");
                                  const assignedOnRaw =
                                    account.assigned_at ||
                                    account.assigned_on ||
                                    "n/a";
                                  const assignedOn =
                                    formatAssignedOnDate(assignedOnRaw);

                                  return (
                                    <div
                                      key={`top-${account.customer_id}`}
                                      className={`mb-1 grid grid-cols-[1.45fr_1.2fr_0.45fr] items-center gap-2 rounded-md border px-2 py-2 transition-colors ${
                                        highlightedAttentionAccountIds.includes(
                                          String(account.customer_id || "")
                                        )
                                          ? "border-[#c4b5fd] bg-[#f8f5ff]"
                                          : "border-transparent bg-gray-50 hover:bg-gray-100"
                                      }`}
                                      style={{ cursor: "pointer" }}
                                      onClick={() =>
                                        toggleAttentionAccountHighlight(
                                          String(account.customer_id || "")
                                        )
                                      }
                                    >
                                      <div className="min-w-0 text-[13px] font-medium text-[#111827]">
                                        <span className="truncate block">
                                          {account.name}
                                        </span>
                                      </div>
                                      {renderSponsorCell(sponsor, assignedOn)}
                                      {renderAccountActionsMenu(
                                        String(account.customer_id || ""),
                                        account.name || "account",
                                        `top:${account.customer_id}`
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            )}

                            {shouldShowRoleTabs &&
                              topBlockAccounts.length === 0 && (
                                <div className="rounded-md border border-purple-200 bg-purple-50/20 px-4 py-4 text-center">
                                  <p className="mb-0 text-base font-medium text-[#a855f7] leading-snug">
                                    {selectedSponsorRoleTab === "primary"
                                      ? "No current primary sponsor assignments"
                                      : "No current secondary sponsor assignments"}
                                  </p>
                                </div>
                              )}

                            {(topBlockAccounts.length > 0 ||
                              shouldShowRoleTabs) &&
                              remainingAccounts.length > 0 && (
                                <div className="border-t border-[#bebed3] my-1" />
                              )}

                            {remainingAccounts.map((account: any) => {
                              const primarySponsor = String(
                                account.primary_sponsors || ""
                              ).trim();
                              const sponsor = [primarySponsor]
                                .filter(Boolean)
                                .join(", ");
                              const assignedOnRaw =
                                account.assigned_at ||
                                account.assigned_on ||
                                "n/a";
                              const assignedOn =
                                formatAssignedOnDate(assignedOnRaw);

                              return (
                                <div
                                  key={account.customer_id}
                                  className={`grid grid-cols-[1.45fr_1.2fr_0.45fr] items-center gap-2 rounded-md border px-3 py-2 transition-colors ${
                                    highlightedAttentionAccountIds.includes(
                                      String(account.customer_id || "")
                                    )
                                      ? "border-[#c4b5fd] bg-[#f8f5ff]"
                                      : "border-transparent bg-[#f5f5fa] hover:border-[#ece3fb]"
                                  }`}
                                  style={{ cursor: "pointer" }}
                                  onClick={() =>
                                    toggleAttentionAccountHighlight(
                                      String(account.customer_id || "")
                                    )
                                  }
                                >
                                  <div className="min-w-0 text-[13px] font-medium text-[#111827]">
                                    <span className="truncate block">
                                      {account.name}
                                    </span>
                                  </div>
                                  {renderSponsorCell(sponsor, assignedOn)}
                                  {renderAccountActionsMenu(
                                    String(account.customer_id || ""),
                                    account.name || "account",
                                    `remaining:${account.customer_id}`
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                ) : (
                  <div className="no-data-found text-center mt-3">
                    No data found
                  </div>
                )}
              </div>
            </div>
            {activeTab === "attention" && (
              <div
                className={`flex h-full min-h-0 min-w-0 flex-1 flex-col self-stretch ${ATTENTION_COLUMNS_H}`}
              >
                {accountFilterTab === "any" && (
                  <AnyTab
                    accountsList={accountsList}
                    searchQuery={searchQuery}
                    highlightedAccountIds={highlightedAttentionAccountIds}
                    onHighlightedAccountIdsChange={
                      syncAnyPortfolioAccountSelection
                    }
                    onSponsorChange={setSelectedSponsorFilter}
                    onBirdsEyeClusterGroupsChange={setBirdsEyeClusterGroups}
                    onSelectedBirdsEyeAccountIdsChange={
                      syncAnyPortfolioAccountSelection
                    }
                    selectedBirdsEyeAccountIds={selectedBirdsEyeAccountIds}
                    briefingParentTab={briefingParentTab}
                    onBriefingParentTabChange={handleBriefingParentTabChange}
                    briefingSubTab={briefingSubTab}
                    onBriefingSubTabChange={handleBriefingSubTabChange}
                    onSaveAsWatchlist={handleOpenCreateWatchlistFromPublicData}
                    onSaveAccountsSuccess={handleSaveAccountsSuccess}
                    showUnavailableKpiData={showUnavailableKpiData}
                    onShowUnavailableKpiDataChange={setShowUnavailableKpiData}
                    kpiDataCounts={kpiDataCounts}
                    onKpiDataCountsChange={handleKpiDataCountsChange}
                    onAccountsWithoutKpiDataChange={
                      handleAccountsWithoutKpiDataChange
                    }
                    showUnavailableBriefingData={showUnavailableBriefingData}
                    onShowUnavailableBriefingDataChange={
                      setShowUnavailableBriefingData
                    }
                    briefingDataCounts={briefingDataCounts}
                    onBriefingDataCountsChange={handleBriefingDataCountsChange}
                    onAccountsWithoutBriefingDataChange={
                      handleAccountsWithoutBriefingDataChange
                    }
                  />
                )}
                {isPortfolioTab && (
                  <PortfolioTab
                    accountsList={portfolioAccountsList}
                    searchQuery={searchQuery}
                    selectedCustomerIds={selectedWatchlistCustomerIds}
                    highlightedAccountIds={highlightedAttentionAccountIds}
                    onHighlightedAccountIdsChange={
                      syncAnyPortfolioAccountSelection
                    }
                    onAccountSelection={handleWatchlistAccountSelection}
                    onTabChange={handleAccountFilterTabChange}
                    onBirdsEyeClusterGroupsChange={setBirdsEyeClusterGroups}
                    onSelectedBirdsEyeAccountIdsChange={
                      syncAnyPortfolioAccountSelection
                    }
                    selectedBirdsEyeAccountIds={selectedBirdsEyeAccountIds}
                    briefingParentTab={briefingParentTab}
                    onBriefingParentTabChange={handleBriefingParentTabChange}
                    briefingSubTab={briefingSubTab}
                    onBriefingSubTabChange={handleBriefingSubTabChange}
                    onSaveAsWatchlist={handleOpenCreateWatchlistFromPublicData}
                    onSaveAccountsSuccess={handleSaveAccountsSuccess}
                    showUnavailableKpiData={showUnavailableKpiData}
                    onShowUnavailableKpiDataChange={setShowUnavailableKpiData}
                    kpiDataCounts={kpiDataCounts}
                    onKpiDataCountsChange={handleKpiDataCountsChange}
                    onAccountsWithoutKpiDataChange={
                      handleAccountsWithoutKpiDataChange
                    }
                    showUnavailableBriefingData={showUnavailableBriefingData}
                    onShowUnavailableBriefingDataChange={
                      setShowUnavailableBriefingData
                    }
                    briefingDataCounts={briefingDataCounts}
                    onBriefingDataCountsChange={handleBriefingDataCountsChange}
                    onAccountsWithoutBriefingDataChange={
                      handleAccountsWithoutBriefingDataChange
                    }
                  />
                )}
                {accountFilterTab === "monitored" && (
                  <MonitoredTab
                    briefingAccounts={monitoredBriefingAccounts}
                    selectedCustomerIds={selectedMonitoredCustomerIds}
                    highlightedAccountIds={
                      monitoredSidebarTab === "others" ||
                      monitoredSidebarTab === "watchlist"
                        ? selectedMonitoredAccountIds
                        : monitoredSidebarTab === "executive"
                          ? selectedExecutiveAccountIds
                          : []
                    }
                    onHighlightedAccountIdsChange={
                      syncMonitoredAccountSelection
                    }
                    selectedWatchlistId={selectedMonitoredWatchlistId}
                    briefingParentTab={briefingParentTab}
                    onBriefingParentTabChange={handleBriefingParentTabChange}
                    briefingSubTab={briefingSubTab}
                    onBriefingSubTabChange={handleBriefingSubTabChange}
                    onSaveAsWatchlist={handleOpenCreateWatchlistFromPublicData}
                    onSaveAccountsSuccess={handleSaveAccountsSuccess}
                    onSelectedBirdsEyeAccountIdsChange={
                      syncMonitoredAccountSelection
                    }
                    selectedBirdsEyeAccountIds={selectedBirdsEyeAccountIds}
                    onBirdsEyeClusterGroupsChange={setBirdsEyeClusterGroups}
                    showUnavailableKpiData={showUnavailableKpiData}
                    onShowUnavailableKpiDataChange={setShowUnavailableKpiData}
                    kpiDataCounts={kpiDataCounts}
                    onKpiDataCountsChange={handleKpiDataCountsChange}
                    onAccountsWithoutKpiDataChange={
                      handleAccountsWithoutKpiDataChange
                    }
                    showUnavailableBriefingData={showUnavailableBriefingData}
                    onShowUnavailableBriefingDataChange={
                      setShowUnavailableBriefingData
                    }
                    briefingDataCounts={briefingDataCounts}
                    onBriefingDataCountsChange={handleBriefingDataCountsChange}
                    onAccountsWithoutBriefingDataChange={
                      handleAccountsWithoutBriefingDataChange
                    }
                  />
                )}
              </div>
            )}
          </div>
          {/* Trailing height so sticky-on-tab can pin while the legend scrolls away. */}
          <div
            aria-hidden
            className="pointer-events-none h-[30vh] w-full shrink-0"
          />
        </div>
      </div>
      <CreateWatchlistModal
        isOpen={isCreateWatchlistModalOpen}
        onClose={() => {
          setIsCreateWatchlistModalOpen(false);
          setCreateWatchlistInitialIds([]);
          setEditWatchListTarget(null);
        }}
        pickerAccounts={createWatchlistPickerAccounts}
        initialSelectedIds={createWatchlistInitialIds}
        editWatchList={editWatchListTarget}
        title="Save Accounts"
        nameLabel="Watchlist Name"
        namePlaceholder="Enter Watchlist Name"
        submitLabel="Create Watchlist"
        onSuccess={handleSaveAccountsSuccess}
      />
      <SaveAccountsSuccessModal
        isOpen={saveAccountsSuccess !== null}
        saveType={saveAccountsSuccess?.type ?? "watchlist"}
        watchlistName={saveAccountsSuccess?.watchlistName}
        onClose={() => setSaveAccountsSuccess(null)}
        onGoToMonitored={handleGoToMonitoredFromSuccess}
      />
    </div>
  );
};

export default Dashboard;
